import { gql } from "@apollo/client";
import { MotionGlobalConfig } from "framer-motion";
import { NetworkStatus } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import {
  useCallback,
  useEffect,
  useInsertionEffect,
  useLayoutEffect,
  useMemo,
  startTransition,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import {
  InboxIcon,
  LayersIcon,
  PauseIcon,
  PlayIcon,
  type LucideIcon,
} from "lucide-react";

import {
  EmailConnectionStatusEnum,
  type RuleDefinitionSuggestionTypeEnum,
  type SearchForDocumentMutation,
  SettlementTreatmentEnum,
  TriageListItemFragment,
  type DocumentFragment,
  type TransactionFragment,
  type TransactionFilter,
  TriageQueueEnum,
  TriageSuggestionStatusEnum,
} from "@/__generated__/graphql";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Icon } from "@/components/atoms/Icon";
import { EmptyState } from "@/components/molecules/EmptyState";
import {
  SELECTABLE_CARD_LIST_FOLD_MS,
  SelectableCardList,
  type SelectableCardListLoadMore,
} from "@/components/molecules/SelectableCardList";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useDevice } from "@/hooks/useDevice";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import {
  findNearestContextNode,
  shouldDeferListFocusReclaim,
  useContextScope,
} from "@/hooks/useContextTree";
import { removeNodesFromConnections } from "@/app/_/subscriptions/writeObjectChangeToCache";
import {
  AppLayoutContentArea,
  AppLayoutContentAreas,
  AppLayoutFooter,
} from "@/layouts/AppLayout";
import { icons } from "@/objects/@icons";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { State } from "@/state";
import { usePageThreadReference } from "@/components/organisms/Thread/Thread.hooks";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { JOB_ICONS, TRIAGE_KEYS, type TriageJobEnum } from "./TriageView.keys";
import {
  TRIAGE_LINK_BALL_FADE_MS,
  TRIAGE_RECONCILE_ROW_PLACEHOLDER,
  TRIAGE_RECONCILE_ROW_SKELETON_COUNT,
  TriageReconcileRow,
  TriageResolutionSearchField,
  TriageSearchChip,
} from "./TriageReconcileRow";
import { TriageHoldNotice } from "./TriageDetailActions";
import { TriageBookLikeCards, TriageBookLikeChanges } from "./TriageBookLike";
import {
  TriageFilterGroup,
  type TriageLikeFilter,
  type TriageQueuePicks,
  TriageQueueView,
} from "./TriageQueueFilter";
import { TriageReconcileManuallyDialog } from "./TriageReconcileManuallyDialog";
import { TriageSuggestionsToolbar } from "./TriageSuggestionsToolbar";
import { TriageLinks } from "./TriageLinks";
import {
  TriageMatchPanelShell,
  TriagePreview,
  type TriageOptionListItem,
} from "./TriageSuggestionsPanel";
import {
  TRIAGE_SUGGESTION_GROUPS,
  TriageSuggestionsTabPanel,
} from "./TriageChangelistProposals";
import {
  buildChangelistProposalPresentation,
  suggestionConclusion,
} from "./TriageChangelistProposalRow";
import {
  firstRecommendationOptionKey,
  type TriageResolutionTab,
} from "./TriageView.resolution";
import {
  buildTriageJournalEntryLines,
  EMPTY_TRIAGE_JOURNAL_RESOLVED,
  journalDocumentsFromSelectionOrFocus,
  journalEntryTotals,
  useTriageDocumentLineItems,
  type TriageJournalLineSource,
  type TriageJournalResolved,
  type TriageJournalSettlementContext,
} from "./TriageJournalEntryPanel";
import {
  TriageDetailContext,
  TriageRowPickedContext,
  TriageViewActionsContext,
  defaultActiveOptionKey,
  defaultTriageSelections,
  resolveActivePreview,
  resolveTriageOptions,
  toggleLinesSuggestion,
  useTriageViewActions,
  type TriageBookLikeOffer,
  type TriageBulkBook,
  type TriageLinedBusiness,
  type TriageGroupMenu,
  type TriageLineUpStatus,
  type TriageBulkMatch,
  type TriageChartedAccountCatalogItem,
  type TriageDetail,
  type TriageDetailContextValue,
  type TriageSelectedTag,
  type TriageOptionCatalog,
  type TriageSuggestionSelections,
  type TriageSuggestionStub,
  type TriageActiveRowStaging,
} from "./TriageView.context";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";

/** An account picked, as Apollo holds it: what the triage needs of it to book - its categories, one chosen on its chip among them. */
const PICKED_ACCOUNT_FRAGMENT = gql`
  fragment TriagePickedAccount on ChartedAccountObject {
    id
    identifier
    name
    path
    fullPath
    description
    categories {
      id
      identifier
      name
      color
    }
  }
`;

// What a Move changes on each card it moves: written to them all as it is
// pressed, and put back on one it refuses (``movePayment``).
const MOVED_FRAGMENT = gql`
  fragment TriageMoved on TransactionObject {
    id
    isBooked
    isProcessing
    entity {
      id
      identifier
      name
    }
    belongsToEntity {
      id
      identifier
      name
    }
    settlementTreatment
  }
`;

/** How narrow the queue may be dragged, beside a payment's matches. */
const TRIAGE_QUEUE_MIN_WIDTH = 320;

/** Similar's matches, as their chip in the panel's search says them. */
const BULK_MATCH_SCOPES: Record<
  TriageBulkMatch,
  { title: string; icon: LucideIcon }
> = {
  transfers: { title: "Match transfers", icon: JOB_ICONS.TRANSFER },
  duplicates: { title: "Match duplicates", icon: JOB_ICONS.DUPLICATE },
  documents: { title: "Match documents", icon: JOB_ICONS.MATCH_DOCUMENT },
};
/** How narrow a payment's matches may be dragged, beside the queue. */
const TRIAGE_MATCH_MIN_WIDTH = 360;
/**
 * Below this the panel's bar has its kinds' menus behind one: its search keeps
 * some 240px beside Hold, Preview and Book.
 */
const MATCH_BAR_FOLD_WIDTH = 620;
/**
 * The least width triage has the queue and a payment's matches side by side
 * in: each at its own least, and the rule between them. Narrower - the chat
 * open beside it on a laptop - they are one at a time, as on a phone.
 */
const TRIAGE_SIDE_BY_SIDE_MIN_WIDTH =
  TRIAGE_QUEUE_MIN_WIDTH + TRIAGE_MATCH_MIN_WIDTH + 1;
/**
 * How much wider than that it has to grow again before they are side by side
 * once more: a panel dragged to and fro about the width they part at does not
 * flick between the two.
 */
const TRIAGE_SIDE_BY_SIDE_SLACK = 40;

function TriageDetail({
  listItem,
  entityIdentifiers,
  onEscape,
  onEngageSuggestions,
  isSuggestionsKeyboardActive,
  suggestionsEngaged,
  reconcilingManually,
  onReconcilingManuallyChange,
  onJournalResolvedChange,
  layoutContent,
  queueFilter = null,
  queueToolbar,
  queueLoading = false,
  isOneAtATime,
  isMatchShown,
  onShowQueue,
  onSwipeToPayment,
  paymentBefore = null,
  paymentAfter = null,
  isSimilar = false,
  onSimilarChange,
  onSimilarRows,
  searchFocus = null,
  onSuggestionsSearchFocus,
}: {
  listItem: TriageListItemFragment | null;
  entityIdentifiers: string[];
  onEscape?: () => void;
  onEngageSuggestions: () => void;
  isSuggestionsKeyboardActive: () => boolean;
  suggestionsEngaged: boolean;
  /** The list open to pick from by hand, if one is. */
  reconcilingManually: TriageResolutionTab | null;
  onReconcilingManuallyChange: (tab: TriageResolutionTab | null) => void;
  onJournalResolvedChange: (resolved: TriageJournalResolved) => void;
  /**
   * The queue's place: the queue - or, Similar on, the payment's payee's
   * others in its stead, their own filters in the bar over both.
   */
  layoutContent?: (
    similarList: ReactNode | null,
    isSimilarShown: boolean,
  ) => ReactNode;
  /** The queue's filter - its search and its queues - Similar's list narrowed by it too. */
  queueFilter?: TransactionFilter | null;
  /**
   * The queue's bar - the layout's, over the queue: its search, its filters
   * and its view on one row. Similar on, Similar's own filters in its Filter.
   */
  queueToolbar?: (
    like: Parameters<typeof TriageFilterGroup>[0]["like"],
  ) => ReactNode;
  /** The queue itself still loading: nothing to pick from yet. */
  queueLoading?: boolean;
  /**
   * The queue and a payment's matches one at a time rather than side by
   * side: on a phone, or with too little room for both.
   */
  isOneAtATime: boolean;
  /** One at a time, the payment's matches in view rather than the queue. */
  isMatchShown: boolean;
  /** Back to the queue from a payment's matches, one at a time. */
  onShowQueue: () => void;
  /** To the next payment in the queue (1) or the one before it (-1). */
  onSwipeToPayment?: (direction: 1 | -1) => void;
  /** The payments either side of it in the queue, slid in from on a phone. */
  paymentBefore?: TriageListItemFragment | null;
  paymentAfter?: TriageListItemFragment | null;
  /** Similar on: its payee's others in the queue's place, its suggestions the bulk list. */
  isSimilar?: boolean;
  onSimilarChange?: (isSimilar: boolean) => void;
  /** Similar on, the payments its list has, once read: the queue narrowed to them. */
  onSimilarRows?: (identifiers: ReadonlySet<string> | null) => void;
  /** Which search the keyboard is in - the queue's, or the panel's - for Tab to go to the other. */
  searchFocus?: "queue" | "suggestions" | null;
  onSuggestionsSearchFocus?: (isFocused: boolean) => void;
}) {
  // The panel's bar, measured: short of room, its kinds' menus are behind one
  // rather than its search going short.
  const [matchBar, setMatchBar] = useState<HTMLDivElement | null>(null);
  const [isMatchBarFolded, setIsMatchBarFolded] = useState(false);
  useEffect(() => {
    if (!matchBar) return;
    const observer = new ResizeObserver(([entry]) =>
      setIsMatchBarFolded(entry.contentRect.width < MATCH_BAR_FOLD_WIDTH),
    );
    observer.observe(matchBar);
    return () => observer.disconnect();
  }, [matchBar]);
  // A finger drawn across the payment under the matches: where it went down,
  // and once it is plainly sideways rather than a scroll, how far it has come.
  const paymentSwipeRef = useRef<{
    x: number;
    y: number;
    isSideways: boolean;
    dx: number;
  } | null>(null);
  const [paymentSwipeX, setPaymentSwipeX] = useState(0);
  // Let go far enough, the way it is sliding - the next payment (1) or the one
  // before (-1) - until it is in; then it is the payment, put in the middle
  // at once (``paymentSnapped``) rather than slid back there.
  const [paymentSliding, setPaymentSliding] = useState<0 | 1 | -1>(0);
  const [paymentSnapped, setPaymentSnapped] = useState(false);
  const apolloClient = useApolloClient();
  const {
    registerEnterSuggestions,
    triggerAccept,
    registerActiveRowStaging,
    openTags: openTagsFromSuggestions,
    focusTriageList,
  } = useTriageViewActions();
  const listItemIdentifier = listItem?.identifier ?? "";
  const { data, loading, error } = useBackendQuery(
    Queries.TRIAGE_DETAIL_QUERY,
    {
      variables: {
        id: identifierAndTypenameToId(listItemIdentifier, "TransactionObject"),
      },
      skip: !listItemIdentifier,
      fetchPolicy: "cache-first",
      // Read again as a suggestion is made, it is asked afresh - not answered
      // by a read already on its way, sent before it was made: a confirmed
      // draft gave way to nothing then (``applyObjectChangesToCache``).
      context: { queryDeduplication: false },
    },
  );
  const detailQueryLoading = Boolean(listItem) && loading;

  const rawNode = useMemo((): TriageDetail | null => {
    if (!listItem) {
      return null;
    }

    const fromQuery =
      data?.node?.__typename === "TransactionObject" ? data.node : null;
    if (fromQuery?.identifier === listItem.identifier) {
      return fromQuery;
    }

    // Prefer the Apollo cache when switching rows so the suggestions panel
    // does not briefly clear while the detail query is in flight.
    try {
      const cached = apolloClient.readQuery({
        query: Queries.TRIAGE_DETAIL_QUERY,
        variables: {
          id: identifierAndTypenameToId(
            listItem.identifier,
            "TransactionObject",
          ),
        },
      });
      const fromCache =
        cached?.node?.__typename === "TransactionObject" ? cached.node : null;
      if (fromCache?.identifier === listItem.identifier) {
        return fromCache;
      }
    } catch {
      // Cache miss — fall through.
    }

    return null;
  }, [apolloClient, data, listItem]);

  const [selections, setSelections] = useState<TriageSuggestionSelections>(
    defaultTriageSelections,
  );
  const [activeOptionKey, setActiveOptionKey] = useState<string | null>(null);
  // The stops opened under themselves - each as it is opened, whatever is
  // picked - cleared for each payment.
  const [expandedOptionKeys, setExpandedOptionKeys] = useState<string[]>([]);
  const setOptionExpanded = useCallback((key: string, expanded: boolean) => {
    setExpandedOptionKeys((current) =>
      expanded
        ? current.includes(key)
          ? current
          : [...current, key]
        : current.filter((one) => one !== key),
    );
  }, []);
  const [optionCatalog, setOptionCatalog] = useState<TriageOptionCatalog>({
    chartedAccountsByIdentifier: {},
    documentsByIdentifier: {},
    transactionsByIdentifier: {},
  });

  const registerCatalogChartedAccounts = useCallback(
    (accounts: TriageChartedAccountCatalogItem[]) => {
      setOptionCatalog((current) => {
        const next = { ...current.chartedAccountsByIdentifier };
        for (const account of accounts) {
          if (account.identifier) {
            next[account.identifier] = account;
          }
        }
        return {
          ...current,
          chartedAccountsByIdentifier: next,
        };
      });
    },
    [],
  );

  const registerCatalogDocuments = useCallback(
    (documents: DocumentFragment[]) => {
      setOptionCatalog((current) => {
        const next = { ...current.documentsByIdentifier };
        for (const document of documents) {
          if (document.identifier) {
            next[document.identifier] = document;
          }
        }
        return {
          ...current,
          documentsByIdentifier: next,
        };
      });
    },
    [],
  );

  const registerCatalogTransactions = useCallback(
    (transactions: TransactionFragment[]) => {
      setOptionCatalog((current) => {
        const next = { ...current.transactionsByIdentifier };
        for (const transaction of transactions) {
          if (transaction.identifier) {
            next[transaction.identifier] = transaction;
          }
        }
        return {
          ...current,
          transactionsByIdentifier: next,
        };
      });
    },
    [],
  );

  const [checkedCategoryOptionIds, setCheckedCategoryOptionIds] = useState<
    Record<string, boolean>
  >({});
  const tagsSeededForIdentifierRef = useRef<string | null>(null);
  const selectedIdentifier = listItem?.identifier ?? "";
  const [selectionScopeIdentifier, setSelectionScopeIdentifier] =
    useState(selectedIdentifier);

  // Reset row-scoped panel state during render so the Match panel commits the
  // new row on the same click, instead of painting the previous selections
  // and then catching up in effects.
  if (selectionScopeIdentifier !== selectedIdentifier) {
    setSelectionScopeIdentifier(selectedIdentifier);
    tagsSeededForIdentifierRef.current = null;
    // Booked into another business, its tags are its row's there.
    const listTags = ((listItem?.movedToTransaction ?? listItem)?.tags ?? [])
      .map((tag) => {
        if (!tag?.id || !tag.identifier) {
          return null;
        }
        return {
          __typename: "TagObject" as const,
          id: tag.id,
          identifier: tag.identifier,
          name: tag.name ?? "",
          color: tag.color,
        };
      })
      .filter((tag): tag is NonNullable<typeof tag> => tag != null);
    setSelections({
      ...defaultTriageSelections(),
      selectedTags: listTags,
    });
    setActiveOptionKey(null);
    setCheckedCategoryOptionIds({});
    // The catalogue of records the cards drew is kept: a lookup by identifier,
    // the picks above reset - emptied here, what fills it again answered
    // nothing new for the next payment of the same business, and a record
    // picked there resolved to nothing.
  }

  useEffect(() => {
    if (!listItem || !rawNode) {
      return;
    }
    if (rawNode.identifier !== listItem.identifier) {
      return;
    }
    if (tagsSeededForIdentifierRef.current === listItem.identifier) {
      return;
    }
    tagsSeededForIdentifierRef.current = listItem.identifier;

    const tags = ((rawNode.movedToTransaction ?? rawNode).tags ?? [])
      .map((tag) => {
        if (!tag?.id || !tag.identifier) {
          return null;
        }
        return {
          __typename: "TagObject" as const,
          id: tag.id,
          identifier: tag.identifier,
          name: tag.name ?? "",
          color: tag.color,
        };
      })
      .filter((tag): tag is NonNullable<typeof tag> => tag != null);

    const tagSuggestion = (
      rawNode.suggestions as TriageSuggestionStub[] | null | undefined
    )?.find(
      (suggestion) => (suggestion.ruleSuggestionTagOptions?.length ?? 0) > 0,
    );
    const tagOptionIds = tags
      .map(
        (tag) =>
          tagSuggestion?.ruleSuggestionTagOptions?.find(
            (option) =>
              option.tag?.identifier === tag.identifier ||
              option.tag?.id === tag.id,
          )?.id,
      )
      .filter((id): id is string => Boolean(id));

    setSelections((current) => ({
      ...current,
      selectedTags: tags,
      tagOptionIds,
    }));
  }, [listItem?.identifier, rawNode]);

  // Let go of - back to the queue - the suggestions let go of the card the
  // keyboard was on. Only then: one chosen with the pointer keeps its place as
  // the payment is read again, a draft becoming its suggestion among them.
  useEffect(() => {
    if (!suggestionsEngaged) {
      setActiveOptionKey(null);
    }
  }, [suggestionsEngaged]);
  useEffect(() => {
    if (!suggestionsEngaged || !rawNode) {
      return;
    }
    setActiveOptionKey(
      (current) =>
        current ??
        firstRecommendationOptionKey(rawNode) ??
        defaultActiveOptionKey(rawNode, defaultTriageSelections()),
    );
  }, [rawNode, suggestionsEngaged]);

  const toggleCategoryOptionChecked = useCallback((optionId: string) => {
    setCheckedCategoryOptionIds((current) => ({
      ...current,
      [optionId]: !current[optionId],
    }));
  }, []);

  const setSelection = useCallback(
    <K extends keyof TriageSuggestionSelections>(
      key: K,
      value:
        | TriageSuggestionSelections[K]
        | ((
            current: TriageSuggestionSelections[K],
          ) => TriageSuggestionSelections[K]),
    ) => {
      setSelections((current) => {
        const nextValue =
          typeof value === "function" ? value(current[key]) : value;
        const next = { ...current, [key]: nextValue };

        if (key === "settlementEntityIdentifier" && !nextValue) {
          next.settlementTags = [];
        }

        return next;
      });
    },
    [],
  );

  // Deleting a journal row in the Preview's entry undoes whatever selection built
  // it; the row itself is rebuilt from `resolved` on the next render.
  const removeJournalLineSource = useCallback(
    (source: TriageJournalLineSource) => {
      if (source.kind === "document") {
        setSelection("documentIdentifiers", (current) =>
          current.filter((identifier) => identifier !== source.identifier),
        );
        setSelection("documentNodeIdsByIdentifier", (current) => {
          const { [source.identifier]: _removed, ...rest } = current;
          return rest;
        });
        // The focused option feeds the preview even when unchecked.
        setActiveOptionKey((current) =>
          current === `document:${source.identifier}` ? null : current,
        );
        return;
      }
      if (source.kind === "transaction") {
        setSelection("transactionIdentifiers", (current) =>
          current.filter((identifier) => identifier !== source.identifier),
        );
        setActiveOptionKey((current) =>
          current === `transaction:${source.identifier}` ? null : current,
        );
        return;
      }
      if (source.kind === "suggestion") {
        setSelection("linesSuggestionIdentifier", null);
        return;
      }
      if (source.kind === "category") {
        setSelection("categoryIdentifier", null);
        return;
      }
      setSelection("chartedAccountIdentifier", null);
    },
    [setActiveOptionKey, setSelection],
  );

  const isDetailReady = Boolean(rawNode);

  const triageForContext = useMemo((): TriageDetail => {
    if (rawNode) {
      return rawNode;
    }
    if (!listItem) {
      return { suggestions: [] } as TriageDetail;
    }
    return {
      id: listItem.id,
      identifier: listItem.identifier,
      description: listItem.description,
      amount: listItem.amount,
      effectiveDate: listItem.effectiveDate,
      entity: listItem.entity,
      belongsToEntity: listItem.belongsToEntity,
      settlementTreatment: listItem.settlementTreatment,
      movedToTransaction: listItem.movedToTransaction,
      chartedAccount: listItem.chartedAccount ?? null,
      financialAccount: listItem.financialAccount ?? null,
      // Whether it is held, and whether the AI is at it, as the queue says -
      // the bar's Hold and its AI buttons answer to them before the detail is in.
      triageHeldAt: listItem.triageHeldAt,
      triageHoldReason: listItem.triageHoldReason,
      triageHeldForViewer: listItem.triageHeldForViewer,
      isProcessing: listItem.isProcessing,
      // List connection already includes changelist stubs — use them while the
      // detail query is in flight so the suggestions panel does not skeleton.
      suggestions: listItem.suggestions ?? [],
    } as TriageDetail;
  }, [listItem, rawNode]);

  // The entry the payment would book, opened under the suggestions' bar by
  // the Preview on it; each payment opens with it shut.
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  useEffect(() => {
    setPreviewOpen(false);
  }, [triageForContext.identifier]);
  const togglePreview = useCallback(() => setPreviewOpen((open) => !open), []);
  // The kind of suggestion the search is narrowed to: that of the card the
  // keyboard was last on, until its chip's x lets it go - cleared for each
  // payment, as what is typed is.
  const [scopedGroupId, setScopedGroupId] = useState<string | null>(null);
  // The mailboxes' chip let go by its x, until the keyboard moves on.
  const [mailboxesDismissed, setMailboxesDismissed] = useState(false);
  // The card of its kind the keyboard is on: a suggestion's, or one of the
  // ways to its document in a mailbox - those are the Match card's.
  const activeGroupId = (() => {
    if (!activeOptionKey) return null;
    // A card's own stop, or the way from it to all there is.
    if (/^(group|more):/.test(activeOptionKey)) {
      return activeOptionKey.slice(activeOptionKey.indexOf(":") + 1);
    }
    if (
      /^(search-mailbox|email-connection-search-result|forward|draft):/.test(
        activeOptionKey,
      )
    ) {
      return "match";
    }
    const job = (triageForContext.suggestions ?? []).find(
      (suggestion) => `lines:${suggestion.identifier}` === activeOptionKey,
    )?.job;
    return (
      TRIAGE_SUGGESTION_GROUPS.find((group) => job && group.jobs.includes(job))
        ?.id ?? null
    );
  })();
  // Narrowed to the card of each stop the keyboard comes to - a click on one
  // included, the first on a payment too - its chips back with it, the Match
  // card's mailboxes as well; an x holds until the keyboard moves on. Onto
  // another card, the search is that card's: what was typed and not searched
  // for goes, and its own words come back as chips.
  const [resolutionSearchText, setResolutionSearchText] = useState("");
  // The words searched for, each made a chip by Enter: by card - kept with it
  // as the keyboard moves on - and under ``all``, with no card named, those
  // every card is searched for.
  const [searchTerms, setSearchTerms] = useState<Record<string, string[]>>({});
  const [lastActiveKey, setLastActiveKey] = useState(activeOptionKey);
  if (activeOptionKey !== lastActiveKey) {
    setLastActiveKey(activeOptionKey);
    if (activeGroupId) {
      if (activeGroupId !== scopedGroupId) setResolutionSearchText("");
      setScopedGroupId(activeGroupId);
      setMailboxesDismissed(false);
    }
  }
  const scopedGroup =
    TRIAGE_SUGGESTION_GROUPS.find((group) => group.id === scopedGroupId) ??
    null;
  // The words the bar holds as chips: its card's, or every card's.
  const barTermsKey = scopedGroupId ?? "all";
  const barTerms = searchTerms[barTermsKey] ?? [];
  // Every word made a chip, the latest last: the one another card is still
  // searched for and this one is not, offered to Tab.
  const [enteredTerms, setEnteredTerms] = useState<string[]>([]);
  const offeredTerm =
    [...enteredTerms]
      .reverse()
      .find(
        (term) =>
          !barTerms.includes(term) &&
          Object.entries(searchTerms).some(
            ([key, words]) => key !== barTermsKey && words.includes(term),
          ),
      ) ?? null;
  // What its cards are drawn with besides its suggestions, asked for with the
  // payment rather than once they are drawn: its business's mailboxes, the
  // drafts in review it would take, and what searches of the mailboxes found
  // for it - each read by its cards from what this has, as these ask it. The
  // panel - its cards and its toolbar - waits for them all, one spinner, and
  // is drawn whole: asked for by the cards after the payment's detail, they
  // were a second spinner, and the toolbar drawn between the two.
  const business = triageForContext.entity?.identifier ?? null;
  const mailboxQuery = useBackendQuery(Queries.EMAIL_CONNECTIONS_QUERY, {
    variables: { entityIdentifiers: business ? [business] : [] },
    fetchPolicy: "cache-and-network",
    skip: !business,
  });
  const draftsQuery = useBackendQuery(Queries.TRIAGE_DRAFT_MATCHES_QUERY, {
    variables: {
      id: identifierAndTypenameToId(
        triageForContext.identifier ?? "",
        "TransactionObject",
      ),
    },
    fetchPolicy: "cache-and-network",
    // Asked afresh when read again, as the payment's suggestions are.
    context: { queryDeduplication: false },
    skip: !triageForContext.identifier,
  });
  const foundQuery = useBackendQuery(
    Queries.TRIAGE_EMAIL_CONNECTION_SEARCH_RESULTS_QUERY,
    {
      variables: { id: triageForContext.id },
      fetchPolicy: "cache-and-network",
      skip: !triageForContext.id,
    },
  );
  // Not yet known for this payment - read again, what there is stays.
  const isCardsLoading =
    (mailboxQuery.loading && !mailboxQuery.data) ||
    (draftsQuery.loading && !draftsQuery.data) ||
    (foundQuery.loading && !foundQuery.data);
  const searchableAddresses = (mailboxQuery.data?.emailConnections?.data ?? [])
    .filter(
      (mailbox) =>
        mailbox.status === EmailConnectionStatusEnum.Connected &&
        mailbox.assignments.some(
          (given) => given.entity?.identifier === business,
        ),
    )
    .map((mailbox) => ({
      emailAddress: mailbox.emailAddress,
      provider: mailbox.provider ?? null,
    }));
  const scopedMailboxes =
    scopedGroup?.id === "match" && !mailboxesDismissed
      ? searchableAddresses
      : null;
  // The keyboard stepped through the suggestions from the panel's search - as
  // the suggestions, drawn, say how.
  const stepSuggestionsRef = useRef<(by: 1 | -1) => void>(() => {});
  // The keyboard in the panel's search: the stops' keys said after Escape.
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const registerStepSuggestions = useCallback((step: (by: 1 | -1) => void) => {
    stepSuggestionsRef.current = step;
  }, []);
  // On a way to the payment's receipt on the Match card - a mailbox to search
  // again, one to connect, what a search found - nothing is picked to book, or
  // to look over first.
  const isOnWayToDocument =
    activeOptionKey?.startsWith("search-mailbox:") === true ||
    activeOptionKey?.startsWith("email-connection-search-result:") === true;

  // The payment moved to another business - and those its Similar lists,
  // ticked: its suggestions worked out again there while the move is asked.
  const { mutate: moveTransactions, loading: moveLoading } = useBackendMutation(
    { mutation: Mutations.MOVE_TRANSACTIONS },
  );
  const isProcessing = Boolean(triageForContext.isProcessing) || moveLoading;
  // The mailbox asked to be searched - or the words every mailbox is - by
  // payment, until the search is back: it says it is being searched
  // meanwhile. A search is the mailbox's, and the AI does not look at the
  // payment while it runs - nor is the payment busy.
  const { mutate: searchForDocument } = useBackendMutation({
    mutation: Mutations.SEARCH_FOR_DOCUMENT,
  });
  const [searchingMailboxes, setSearchingMailboxes] = useState<
    Record<string, TriageDetailContextValue["searching"]>
  >({});
  const [searchFailures, setSearchFailures] = useState<
    Record<string, NonNullable<TriageDetailContextValue["searchFailure"]>>
  >({});
  const searchMailbox = useCallback(
    (connectionIdentifier: string | null, query?: string) => {
      const identifier = triageForContext.identifier;
      const payment = triageForContext.id;
      if (!identifier) return;
      setSearchingMailboxes((current) => ({
        ...current,
        [payment]: { connectionIdentifier, query },
      }));
      // Asked again: what the last could not do is not said of this one.
      setSearchFailures(({ [payment]: _, ...rest }) => rest);
      const sayFailed = (message: string) =>
        setSearchFailures((current) => ({
          ...current,
          [payment]: current[payment] ?? {
            connectionIdentifier,
            query,
            message,
          },
        }));
      // Done, unless a search asked for after it is under way in its place.
      const stopSearching = () =>
        setSearchingMailboxes(({ [payment]: was, ...rest }) =>
          was?.query === query &&
          was?.connectionIdentifier === connectionIdentifier
            ? rest
            : { ...rest, [payment]: was },
        );
      void searchForDocument({
        // What it found comes back with the payment, on the mailbox card.
        variables: {
          input: {
            transactionIdentifier: identifier,
            connectionIdentifier,
            query,
          },
        },
        callbacks: {
          onSuccess: (data) => {
            stopSearching();
            // Each mailbox as the search left it: one it could not run in says
            // why, one it did says nothing of an earlier failure.
            void apolloClient.refetchQueries({
              include: [Queries.EMAIL_CONNECTIONS_QUERY],
            });
            const searched =
              data as SearchForDocumentMutation["searchForDocument"]["data"];
            // Nothing to show for it: said, rather than left to be noticed -
            // on the Match card, for what is typed.
            if (
              !query &&
              !searched?.emailConnectionSearchResults.some(
                (result) => !result.query,
              )
            ) {
              State.getState().setSnackbarMessage(
                "Nothing in that mailbox matches this payment.",
              );
            }
          },
          // Refused, the mailboxes are read again: one its provider signed
          // out says so, and is reconnected rather than searched. And it says
          // it could not search, and why - on the card, where what it found
          // would be, not that nothing was found.
          onError: (_, errors) => {
            sayFailed(
              errors?.genericErrors?.[0]?.message ??
                "Your mailboxes could not be searched. Try again.",
            );
            stopSearching();
            void apolloClient.refetchQueries({
              include: [Queries.EMAIL_CONNECTIONS_QUERY],
            });
          },
        },
      });
    },
    [
      apolloClient,
      searchForDocument,
      triageForContext.id,
      triageForContext.identifier,
    ],
  );
  // What a search found, open to preview: closed as the payment changes.
  const [
    previewingEmailConnectionSearchResult,
    setPreviewingEmailConnectionSearchResult,
  ] = useState<string | null>(null);
  useEffect(
    () => setPreviewingEmailConnectionSearchResult(null),
    [triageForContext.id],
  );
  // What was imported from a mailbox and is being read in its chat, by
  // payment, until the draft it is read into is in.
  const [
    importingEmailConnectionSearchResults,
    setImportingEmailConnectionSearchResults,
  ] = useState<
    Record<string, NonNullable<TriageOptionListItem["searchResult"]>>
  >({});
  const setImportingEmailConnectionSearchResult = useCallback(
    (importing: TriageOptionListItem["searchResult"] | null) => {
      const payment = triageForContext.id;
      setImportingEmailConnectionSearchResults(({ [payment]: _, ...rest }) =>
        importing ? { ...rest, [payment]: importing } : rest,
      );
    },
    [triageForContext.id],
  );
  const { mutate: dismissEmailConnectionSearchResults } = useBackendMutation({
    mutation: Mutations.DISMISS_EMAIL_CONNECTION_SEARCH_RESULTS,
  });
  // The payment said to be another business's - or its own again - from the
  // suggestions' toolbar: its suggestions are worked out again there by the
  // time the move answers, and read at once. It says so till they are.
  const [movingPaymentTo, setMovingPaymentTo] = useState<
    Record<string, string>
  >({});
  const movePayment = useCallback(
    (
      entityIdentifier: string,
      treatment: SettlementTreatmentEnum | null,
      others: string[] = [],
    ) => {
      const payment = triageForContext.id;
      const identifier = triageForContext.identifier;
      // Moved again while its suggestions are worked out there - as they are
      // after every move - it goes: only one move of it at a time.
      if (!payment || !identifier || movingPaymentTo[payment]) return;
      setMovingPaymentTo((current) => ({
        ...current,
        [payment]: entityIdentifier,
      }));
      const doneMoving = () =>
        setMovingPaymentTo(({ [payment]: _, ...rest }) => rest);
      // What was picked stays picked: made afresh there, its kind's surest.
      const pickedJob = (triageForContext.suggestions ?? []).find(
        (row) => row.identifier === selections.linesSuggestionIdentifier,
      )?.job;
      // Every card it moves says so as it is pressed - the business it is for
      // now, and, waiting, at work on what it is offered there - not one after
      // another as the server writes each. Each as it was, to put back one the
      // move refuses.
      const { cache } = apolloClient;
      const cardOf = (one: string) =>
        cache.identify({
          __typename: "TransactionObject",
          id: identifierAndTypenameToId(one, "TransactionObject"),
        });
      const there = State.getState().entities.find(
        (entity) => entity.identifier === entityIdentifier,
      );
      type MovedCard = Pick<
        TriageListItemFragment,
        | "__typename"
        | "id"
        | "isBooked"
        | "isProcessing"
        | "entity"
        | "belongsToEntity"
        | "settlementTreatment"
      >;
      const was = new Map(
        [identifier, ...others].map((one) => [
          one,
          cache.readFragment<MovedCard>({
            id: cardOf(one),
            fragment: MOVED_FRAGMENT,
          }),
        ]),
      );
      for (const [one, card] of was) {
        if (card == null) continue;
        const isOwn = card.entity?.identifier === entityIdentifier;
        cache.writeFragment<MovedCard>({
          id: cardOf(one),
          fragment: MOVED_FRAGMENT,
          data: {
            ...card,
            belongsToEntity: isOwn
              ? null
              : {
                  __typename: "EntityObject",
                  id: identifierAndTypenameToId(
                    entityIdentifier,
                    "EntityObject",
                  ),
                  identifier: entityIdentifier,
                  name: there?.name ?? "",
                },
            // Booked, it is booked again there - a loan, unless it was said.
            settlementTreatment: isOwn
              ? null
              : card.isBooked
                ? (treatment ?? SettlementTreatmentEnum.Loan)
                : treatment,
            isProcessing:
              card.isProcessing ||
              (!card.isBooked &&
                card.belongsToEntity?.identifier !== entityIdentifier),
          },
        });
      }
      const putBack = (refused: string[]) => {
        for (const one of refused) {
          const card = was.get(one);
          if (card != null) {
            cache.writeFragment<MovedCard>({
              id: cardOf(one),
              fragment: MOVED_FRAGMENT,
              data: card,
            });
          }
        }
      };
      void moveTransactions({
        variables: {
          input: {
            transactionIdentifiers: [identifier, ...others],
            entityIdentifier,
            treatment,
          },
        },
        callbacks: {
          onSuccess: (answer: {
            done: string[];
            refused: { transaction: string; reason: string }[];
          }) => {
            // Moved as it answers: its Move is free again at once, the
            // payment read again behind it.
            doneMoving();
            putBack(answer.refused.map((refusal) => refusal.transaction));
            if (answer.refused.length > 0) {
              State.getState().setSnackbarMessage(
                `${answer.refused.length} not moved: ${answer.refused[0].reason}`,
              );
            }
            // Those moved with it, read again where the queue has them.
            if (others.length > 0) {
              void apolloClient.refetchQueries({
                include: [Queries.TRIAGE_CONNECTION_QUERY],
              });
            }
            void apolloClient
              .query({
                query: Queries.TRIAGE_DETAIL_QUERY,
                variables: { id: payment },
                fetchPolicy: "network-only",
              })
              .then(({ data }) => {
                const node = data?.node;
                const again =
                  pickedJob && node?.__typename === "TransactionObject"
                    ? [...(node.suggestions ?? [])]
                        .filter(
                          (row) =>
                            row.job === pickedJob &&
                            row.status === TriageSuggestionStatusEnum.Pending,
                        )
                        .sort(
                          (left, right) =>
                            Number(right.confidence ?? 0) -
                            Number(left.confidence ?? 0),
                        )[0]
                    : undefined;
                if (
                  again &&
                  again.identifier !== selections.linesSuggestionIdentifier
                ) {
                  toggleLinesSuggestion(again, null, setSelection);
                }
              });
          },
          onError: () => {
            doneMoving();
            putBack([...was.keys()]);
          },
        },
      });
    },
    [
      apolloClient,
      moveTransactions,
      movingPaymentTo,
      selections.linesSuggestionIdentifier,
      setSelection,
      triageForContext.id,
      triageForContext.identifier,
      triageForContext.suggestions,
    ],
  );
  // A kind's suggestions worked out again by its rules - by its menu in the
  // toolbar, or Enter on a card with none - each card on its own: asked for while another is,
  // it is worked out beside it. It says so, by payment, till what it comes to
  // is read.
  const { mutate: refreshTriageSuggestions } = useBackendMutation({
    mutation: Mutations.REFRESH_TRIAGE_SUGGESTIONS,
  });
  const [rerunningByPayment, setRerunningByPayment] = useState<
    Record<string, string[]>
  >({});
  // The same, as each is asked for: a card asked for twice before it is drawn
  // spinning is still asked once.
  const rerunningRef = useRef<Set<string>>(new Set());
  const rerunSuggestions = useCallback(
    (group: { id: string; jobs: readonly string[] }) => {
      const payment = triageForContext.id;
      const identifier = triageForContext.identifier;
      // Booked, nothing is left to suggest.
      if (!payment || !identifier || triageForContext.isBooked) return;
      const key = `${payment}:${group.id}`;
      if (rerunningRef.current.has(key)) return;
      rerunningRef.current.add(key);
      setRerunningByPayment((current) => ({
        ...current,
        [payment]: [...(current[payment] ?? []), group.id],
      }));
      const doneRerunning = () => {
        rerunningRef.current.delete(key);
        setRerunningByPayment(({ [payment]: ids = [], ...rest }) => {
          const left = ids.filter((id) => id !== group.id);
          return left.length > 0 ? { ...rest, [payment]: left } : rest;
        });
      };
      void refreshTriageSuggestions({
        variables: {
          input: {
            transactionIdentifier: identifier,
            kinds: group.jobs as RuleDefinitionSuggestionTypeEnum[],
          },
        },
        callbacks: {
          // Its rules have worked it out by the time it answers: read at once.
          onSuccess: () => {
            void apolloClient
              .query({
                query: Queries.TRIAGE_DETAIL_QUERY,
                variables: { id: payment },
                fetchPolicy: "network-only",
              })
              .finally(doneRerunning);
          },
          onError: doneRerunning,
        },
      });
    },
    [
      apolloClient,
      refreshTriageSuggestions,
      triageForContext.id,
      triageForContext.identifier,
      triageForContext.isBooked,
    ],
  );
  // Read by ⌘↵, as it is when pressed.
  const rerunSuggestionsRef = useRef(rerunSuggestions);
  rerunSuggestionsRef.current = rerunSuggestions;
  const rerunning = rerunningByPayment[triageForContext.id];
  const rerunningSuggestions = useMemo(() => rerunning ?? [], [rerunning]);
  // Turned down from its card, or with R on the one the keyboard is on: the
  // server answers with the payment's suggestions still on offer. Picked, it
  // is let go with it.
  const { mutate: rejectTriageSuggestion } = useBackendMutation({
    mutation: Mutations.REJECT_TRIAGE_SUGGESTION,
  });
  const rejectSuggestion = useCallback(
    (suggestionIdentifier: string) => {
      const suggestions = (triageForContext.suggestions ??
        []) as TriageSuggestionStub[];
      const suggestion = suggestions.find(
        (row) => row.identifier === suggestionIdentifier,
      );
      if (!suggestion) return;
      // A row stands for every rule that came to the same, and a card for
      // every way to post it in another business that lends it there alike:
      // turned down, they all are, or the next would take its place.
      const conclusion = suggestionConclusion(suggestion);
      const payerSide = buildChangelistProposalPresentation(
        suggestion,
        triageForContext,
      ).payerSide;
      const agreeing = suggestions.filter((row) =>
        payerSide != null
          ? buildChangelistProposalPresentation(row, triageForContext)
              .payerSide === payerSide
          : suggestionConclusion(row) === conclusion,
      );
      const picked = agreeing.find(
        (row) => row.identifier === selections.linesSuggestionIdentifier,
      );
      if (picked) {
        toggleLinesSuggestion(
          picked,
          selections.linesSuggestionIdentifier,
          setSelection,
        );
      }
      for (const row of agreeing) {
        void rejectTriageSuggestion({
          variables: { input: { suggestionIdentifier: row.identifier } },
        });
      }
    },
    [
      rejectTriageSuggestion,
      selections.linesSuggestionIdentifier,
      setSelection,
      triageForContext,
    ],
  );
  // The stop the keyboard is on, opened or shut - where it opens to anything,
  // as a list's row opens by its disclosure: whether that changed anything.
  const openActiveRef = useRef<(open: boolean) => boolean>(() => false);
  openActiveRef.current = (open) => {
    const key = activeOptionKey;
    if (!key || open === expandedOptionKeys.includes(key)) return false;
    if (
      open &&
      !document.querySelector(
        `[data-option-key="${CSS.escape(key)}"][data-expandable]`,
      )
    ) {
      return false;
    }
    setOptionExpanded(key, open);
    return true;
  };
  const rejectActiveRef = useRef(() => {});
  rejectActiveRef.current = () => {
    // Booked, what booked it stands: nothing is left to turn down.
    if (activeOptionKey?.startsWith("lines:") && !triageForContext.isBooked) {
      rejectSuggestion(activeOptionKey.slice("lines:".length));
    } else if (activeOptionKey?.startsWith("draft:")) {
      // A draft in review, by its own Reject: its change is its chat's.
      document
        .querySelector<HTMLElement>('[data-testid="triage-draft-reject"]')
        ?.click();
    } else if (
      activeOptionKey?.startsWith("email-connection-search-result:") &&
      triageForContext.identifier
    ) {
      // What a search found, dismissed - all of it: it was one search's.
      void dismissEmailConnectionSearchResults({
        variables: {
          input: { transactionIdentifier: triageForContext.identifier },
        },
      });
    }
  };

  // What the suggestions are searched for (declared above). It is typed in the
  // panel's toolbar - the layout's, over the panel - and cleared for each
  // payment.
  const [resolutionSearchFor, setResolutionSearchFor] = useState(
    listItem?.identifier ?? null,
  );
  if (resolutionSearchFor !== (listItem?.identifier ?? null)) {
    setResolutionSearchFor(listItem?.identifier ?? null);
    setResolutionSearchText("");
    setSearchTerms({});
    setExpandedOptionKeys([]);
    setScopedGroupId(null);
  }

  const resolved = useMemo(() => {
    // The account picked, whatever drew it: from the catalogue - else as
    // Apollo holds it, every account on screen there by the query that drew
    // it. Never only an effect's registering: a catalogue emptied for the
    // next payment while the query that fills it answers nothing new left
    // the pick unresolved, and Book greyed with it ticked.
    const picked = selections.chartedAccountIdentifier;
    const cached =
      picked && !optionCatalog.chartedAccountsByIdentifier[picked]
        ? apolloClient.cache.readFragment<TriageChartedAccountCatalogItem>({
            id: apolloClient.cache.identify({
              __typename: "ChartedAccountObject",
              id: identifierAndTypenameToId(picked, "ChartedAccountObject"),
            }),
            fragment: PICKED_ACCOUNT_FRAGMENT,
            returnPartialData: true,
          })
        : null;
    return resolveTriageOptions(
      triageForContext,
      selections,
      cached?.identifier
        ? {
            ...optionCatalog,
            chartedAccountsByIdentifier: {
              ...optionCatalog.chartedAccountsByIdentifier,
              [cached.identifier]: cached,
            },
          }
        : optionCatalog,
    );
  }, [apolloClient, optionCatalog, triageForContext, selections]);

  // The payment picked is offered to the chat, while it is open, as the
  // page's reference. Taken back when another is picked, the chat shuts, or
  // the page goes.
  const paymentIdentifier = listItem?.identifier ?? null;
  const paymentName = listItem?.description || "Untitled";
  usePageThreadReference(
    paymentIdentifier
      ? {
          id: identifierAndTypenameToId(paymentIdentifier, "TransactionObject"),
          identifier: paymentIdentifier,
          name: paymentName,
          typename: "TransactionObject",
        }
      : null,
  );

  const activePreview = useMemo(() => {
    return resolveActivePreview(triageForContext, activeOptionKey);
  }, [triageForContext, activeOptionKey]);

  // A draft in review, picked or the one the keyboard is on: the entry it
  // would book once accepted, read from what the suggestions list asked for.
  const previewedDraft =
    selections.draftDocumentIdentifier ??
    (activeOptionKey?.startsWith("draft:")
      ? activeOptionKey.slice("draft:".length)
      : null);
  const draftMatches = useBackendQuery(Queries.TRIAGE_DRAFT_MATCHES_QUERY, {
    variables: {
      id: identifierAndTypenameToId(
        triageForContext.identifier ?? "",
        "TransactionObject",
      ),
    },
    fetchPolicy: "cache-first",
    skip: previewedDraft == null || !triageForContext.identifier,
  });
  const draftInReview = useMemo(() => {
    const node = draftMatches.data?.node;
    return (
      (node && "draftMatches" in node ? node.draftMatches : []).find(
        (match) => match.document?.identifier === previewedDraft,
      )?.inReview ?? null
    );
  }, [draftMatches.data, previewedDraft]);

  const journalResolvedForBalance = useMemo((): TriageJournalResolved => {
    // Use raw selections — resolved options can lag one frame until catalog
    // registration catches up with document / account / transaction picks.
    const hasManualCoding =
      selections.categoryIdentifier != null ||
      selections.chartedAccountIdentifier != null ||
      (selections.transactionIdentifiers?.length ?? 0) > 0 ||
      ((selections.documentIdentifiers?.length ?? 0) > 0 &&
        selections.linesSuggestionIdentifier == null);

    // Changelist-only preview when a proposal is selected and nothing manual yet.
    if (resolved.linesSuggestion != null && !hasManualCoding) {
      return {
        ...EMPTY_TRIAGE_JOURNAL_RESOLVED,
        linesSuggestion: resolved.linesSuggestion,
      };
    }
    // A draft's, with nothing else picked: its lines are all that is read of
    // the suggestion it is in review as.
    if (draftInReview != null && !hasManualCoding) {
      return {
        ...EMPTY_TRIAGE_JOURNAL_RESOLVED,
        linesSuggestion: draftInReview as TriageSuggestionStub,
      };
    }

    const documents = journalDocumentsFromSelectionOrFocus({
      documentOption: resolved.documentOption,
      documentOptions: resolved.documentOptions,
      focusedDocumentOption:
        activePreview.kind === "document" ? activePreview.documentOption : null,
    });

    // The Preview's entry: show the full booking picture across tabs (documents,
    // peers, category, and charted account). Do not scope by activeResolutionTab —
    // journalResolvedForTab is for per-tab option browsing only.
    return {
      categoryOption: resolved.categoryOption,
      documentOption: documents.documentOption,
      documentOptions: documents.documentOptions,
      chartedAccountOption: resolved.chartedAccountOption,
      selectedTransactions: resolved.selectedTransactions ?? [],
      linesSuggestion: null,
    };
  }, [activePreview, draftInReview, resolved, selections]);

  const { lineItems: balanceDocumentLineItems } = useTriageDocumentLineItems(
    journalResolvedForBalance,
  );

  // The other business its lines settle into: as coded by hand, else the one
  // its Move said it is for, or it was booked into - what its Book settles it
  // in and checks it against, its side drawn beside the payer's.
  const journalSettlement =
    useMemo((): TriageJournalSettlementContext | null => {
      const payer = triageForContext.entity ?? listItem?.entity;
      const moved =
        triageForContext.belongsToEntity ?? listItem?.belongsToEntity;
      const settlementEntityIdentifier =
        selections.settlementEntityIdentifier ?? moved?.identifier ?? null;
      if (
        !settlementEntityIdentifier ||
        !payer?.identifier ||
        settlementEntityIdentifier === payer.identifier
      ) {
        return null;
      }
      const settlementEntity = State.getState().entities.find(
        (entity) => entity.identifier === settlementEntityIdentifier,
      );
      return {
        settlementEntityIdentifier,
        settlementEntityName:
          settlementEntity?.name ??
          (moved?.identifier === settlementEntityIdentifier
            ? moved.name
            : null) ??
          null,
        payerEntityName: payer.name,
      };
    }, [
      listItem?.entity,
      listItem?.belongsToEntity,
      selections.settlementEntityIdentifier,
      triageForContext.entity,
      triageForContext.belongsToEntity,
    ]);

  const journalEntryIsBalanced = useMemo(() => {
    const lines = buildTriageJournalEntryLines(
      triageForContext,
      journalResolvedForBalance,
      balanceDocumentLineItems,
      journalSettlement,
    );
    return journalEntryTotals(lines).isBalanced;
  }, [
    balanceDocumentLineItems,
    journalResolvedForBalance,
    journalSettlement,
    triageForContext,
  ]);

  const journalSource = useMemo(() => {
    if (!listItem) {
      return triageForContext;
    }
    const detailFinancialCharted =
      triageForContext.financialAccount != null &&
      "chartedAccount" in triageForContext.financialAccount
        ? triageForContext.financialAccount.chartedAccount
        : undefined;
    const hasDetailBankRef =
      Boolean(detailFinancialCharted?.id) ||
      Boolean(triageForContext.chartedAccount?.id);
    if (!hasDetailBankRef) {
      return {
        ...triageForContext,
        chartedAccount:
          listItem.chartedAccount ?? triageForContext.chartedAccount,
        financialAccount:
          listItem.financialAccount ?? triageForContext.financialAccount,
      };
    }
    return triageForContext;
  }, [listItem, triageForContext]);

  // Similar, as the suggestions' L turns it on and off.
  const similarRef = useRef({ is: isSimilar, change: onSimilarChange });
  similarRef.current = { is: isSimilar, change: onSimilarChange };
  // The payment's payee's others, as its Book works them out - listed in the
  // queue's place while Similar is on - and what the bulk list's row picked
  // has Book do to them.
  const [bookLikeOffer, setBookLikeOffer] =
    useState<TriageBookLikeOffer | null>(null);
  const [bulkBook, setBulkBook] = useState<TriageBulkBook | null>(null);
  // The match picked in it, and the list in the queue's place it lines up
  // beside.
  const [bulkMatch, setBulkMatch] = useState<TriageBulkMatch | null>(null);
  // The row picked in it, there alone: what the cards stage.
  const [bulkPick, setBulkPick] = useState<string | null>(null);
  // Another payment open: no match picked in its list, nor a row. (Said here,
  // where they are kept - not as the list goes, which a list drawn again
  // takes for going.)
  useEffect(() => {
    setBulkMatch(null);
    setBulkPick(null);
  }, [triageForContext.identifier]);
  // How the suggestion picked settles in another business where no other way
  // is said: the way its card recommends, as the server books it
  // (``recommendedTreatment``, none for one in its own business alone) - said
  // with the suggestion, for its row, its Move and its Book.
  const pickedSuggestion = (triageForContext.suggestions ?? []).find(
    (suggestion) =>
      suggestion.identifier === selections.linesSuggestionIdentifier,
  );
  const recommendedTreatment = pickedSuggestion?.recommendedTreatment ?? null;
  const [linedUp, setLinedUp] = useState<ReadonlyMap<
    string,
    TriageLinedBusiness
  > | null>(null);
  // Each kind's own actions, for its button in the toolbar: set by the
  // suggestions as they are drawn.
  const [groupMenus, setGroupMenus] = useState<TriageGroupMenu[] | null>(null);
  const [lineUpStatus, setLineUpStatus] = useState<TriageLineUpStatus | null>(
    null,
  );
  const [lineUpRoom, setLineUpRoom] = useState(0);
  // Similar turned on: the queue narrowed to the payee's others in it, the
  // rest folding away, and once they have and its payments are read, its list
  // in the queue's place - till then the queue, narrowed, still on the payment.
  // Turned off, the other way about: its balls fade out, then the queue is
  // back in its place narrowed to the same payments, then the rest unfold
  // back in around them.
  const [similarClosing, setSimilarClosing] = useState<"balls" | "rows" | null>(
    null,
  );
  const similarBookLike = isSimilar ? bookLikeOffer?.bookLike : undefined;
  const narrowingBookLike =
    isSimilar || similarClosing ? bookLikeOffer?.bookLike : undefined;
  // Still being read, the payment alone: the rest fold away all the same.
  const similarRowsKey =
    narrowingBookLike && !narrowingBookLike.isLoading
      ? [
          ...narrowingBookLike.shown,
          ...(narrowingBookLike.pinned ? [narrowingBookLike.pinned] : []),
        ]
          .map((row) => row.card.identifier)
          .join(" ")
      : (isSimilar || similarClosing) && listItemIdentifier
        ? listItemIdentifier
        : null;
  const [isSimilarListed, setIsSimilarListed] = useState(false);
  const [similarListElement, setSimilarListElement] =
    useState<HTMLElement | null>(null);
  // The payment's row in the list on screen - the queue, or Similar's in its
  // place - and where it stands in it, as if not held at the top or the foot:
  // the list it gives way to is scrolled as far past it, so nothing jumps as
  // they change over.
  const measurePaymentRow = useCallback(
    (isSimilarList: boolean) => {
      const row =
        document
          .getElementById("triage-queue")
          ?.querySelector(
            isSimilarList
              ? `[data-testid='triage-similar'] [data-identifier='${listItemIdentifier}']`
              : `[data-testid='triage-list'] [data-triage-id='${listItem?.id ?? ""}']`,
          )
          ?.closest<HTMLElement>("[data-selectable-row]") ?? null;
      const scroller = row?.closest<HTMLElement>("[data-selectable-card-list]");
      if (!row || !scroller) return null;
      const position = row.style.position;
      row.style.position = "static";
      const offset = row.offsetTop;
      row.style.position = position;
      return {
        scroller,
        top:
          scroller.getBoundingClientRect().top +
          scroller.clientTop +
          offset -
          scroller.scrollTop,
      };
    },
    [listItem?.id, listItemIdentifier],
  );
  const paymentTopRef = useRef<{ top: number } | null>(null);
  useLayoutEffect(() => {
    const was = paymentTopRef.current;
    if (was == null) return;
    const now = measurePaymentRow(isSimilarListed);
    // Still being read: once its row is drawn.
    if (!now) return;
    paymentTopRef.current = null;
    now.scroller.scrollTop += now.top - was.top;
    // Back to the queue: each of its rows held where Similar's list showed
    // that payment - till the rest of the queue is back round them
    // (``TriageView`` lets them go then; soon, if not) - so they move once,
    // from there to where they are in the whole queue. On top of framer's
    // glides as rows come back, not in place of them.
    const tops = similarTopsRef.current;
    similarTopsRef.current = null;
    if (isSimilarListed || !tops) return;
    const slides = [
      ...now.scroller.querySelectorAll<HTMLElement>("[data-selectable-row]"),
    ].flatMap((row) => {
      const id = row
        .querySelector("[data-triage-id]")
        ?.getAttribute("data-triage-id");
      const top = id ? tops.get(atob(id).split(":")[1] ?? "") : undefined;
      const by = top == null ? 0 : top - row.getBoundingClientRect().top;
      if (Math.abs(by) < 1) return [];
      const slide = row.animate(
        [
          { transform: `translateY(${by}px)` },
          { transform: "translateY(0px)" },
        ],
        {
          duration: SELECTABLE_CARD_LIST_FOLD_MS / 2,
          easing: "cubic-bezier(0.4, 0, 0.2, 1)",
          composite: "add",
        },
      );
      slide.pause();
      return [slide];
    });
    heldSlidesTimeoutRef.current = setTimeout(() => {
      for (const slide of slides) {
        if (slide.playState === "paused") slide.play();
      }
    }, SELECTABLE_CARD_LIST_FOLD_MS * 2);
  });
  const heldSlidesTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(heldSlidesTimeoutRef.current), []);
  const similarTopsRef = useRef<ReadonlyMap<string, number> | null>(null);
  useEffect(() => {
    if (similarClosing === "balls") {
      // Once its balls have faded out - not after a set time, which a busy
      // page can spend before they have moved at all - or, none to fade, soon.
      let isDone = false;
      const goOn = () => {
        if (isDone) return;
        isDone = true;
        paymentTopRef.current = measurePaymentRow(true);
        similarTopsRef.current = new Map(
          [
            ...(similarListElement?.querySelectorAll<HTMLElement>(
              "[data-testid='triage-book-like-row']",
            ) ?? []),
          ].map((card) => [
            card.dataset.identifier ?? "",
            // Its row's, as the queue's are measured: not the card in it.
            (
              card.closest("[data-selectable-row]") ?? card
            ).getBoundingClientRect().top,
          ]),
        );
        setIsSimilarListed(false);
        setSimilarClosing("rows");
      };
      const onFaded = (event: TransitionEvent) => {
        if (
          event.propertyName === "opacity" &&
          (event.target as HTMLElement).hasAttribute("data-link-from")
        ) {
          goOn();
        }
      };
      similarListElement?.addEventListener("transitionend", onFaded);
      const timeout = setTimeout(goOn, TRIAGE_LINK_BALL_FADE_MS * 3);
      return () => {
        similarListElement?.removeEventListener("transitionend", onFaded);
        clearTimeout(timeout);
      };
    }
    if (similarClosing === "rows") {
      // The queue drawn as it was narrowed first, then the rest put back -
      // the keys back in it, on the payment, now it is there to take them.
      focusTriageList();
      const frame = requestAnimationFrame(() => setSimilarClosing(null));
      return () => cancelAnimationFrame(frame);
    }
  }, [focusTriageList, measurePaymentRow, similarClosing, similarListElement]);
  useEffect(() => {
    if (similarRowsKey == null) return;
    onSimilarRows?.(new Set(similarRowsKey.split(" ")));
    return () => onSimilarRows?.(null);
  }, [onSimilarRows, similarRowsKey]);
  useEffect(() => {
    if (!isSimilar || isSimilarListed) return;
    const timeout = setTimeout(() => {
      paymentTopRef.current = measurePaymentRow(false);
      setIsSimilarListed(true);
    }, SELECTABLE_CARD_LIST_FOLD_MS);
    return () => clearTimeout(timeout);
  }, [isSimilar, isSimilarListed, measurePaymentRow]);
  // Similar turned on with a kind of suggestion picked - its chip in the
  // panel's search - opens on that kind's match.
  const [wasSimilar, setWasSimilar] = useState(isSimilar);
  if (isSimilar !== wasSimilar) {
    setWasSimilar(isSimilar);
    if (isSimilar) {
      setSimilarClosing(null);
      // The rest fold away first - drawn at once, its list at once.
      setIsSimilarListed(MotionGlobalConfig.skipAnimations);
    } else if (isSimilarListed && !MotionGlobalConfig.skipAnimations) {
      // Its list stays till its balls have faded out.
      setSimilarClosing("balls");
    } else {
      setIsSimilarListed(false);
    }
    // Off: no match picked in its list, nor a row.
    if (!isSimilar) {
      setBulkMatch(null);
      setBulkPick(null);
    }
    // Opened on the match of the suggestion picked - a document, a transfer,
    // a copy - its kind's line-up, that one lined up with the payment in it;
    // with none picked, of the card the keyboard was last on. A posting
    // picked, on its row in the bulk list, picked there too.
    const pickedJob = (triageForContext.suggestions ?? []).find(
      (suggestion) =>
        suggestion.identifier === selections.linesSuggestionIdentifier,
    )?.job;
    const pickedGroup = TRIAGE_SUGGESTION_GROUPS.find(
      (group) => pickedJob && group.jobs.includes(pickedJob),
    )?.id;
    const match = isSimilar
      ? (
          {
            match: "documents",
            duplicate: "duplicates",
            transfer: "transfers",
          } as const
        )[(pickedGroup ?? scopedGroupId) as "match" | "duplicate" | "transfer"]
      : undefined;
    if (match) setBulkMatch(match);
    if (isSimilar && pickedGroup === "post") {
      setBulkPick(`post:${selections.linesSuggestionIdentifier}`);
    }
  }
  const detailContextValue = useMemo(
    () => ({
      triage: triageForContext,
      selections,
      setSelection,
      activeOptionKey,
      setActiveOptionKey,
      activePreview,
      resolved,
      checkedCategoryOptionIds,
      toggleCategoryOptionChecked,
      suggestionsEngaged,
      engageSuggestions: onEngageSuggestions,
      isSuggestionsKeyboardActive: () => isSuggestionsKeyboardActive(),
      isDetailLoading: detailQueryLoading && !isDetailReady,
      detailQueryLoading,
      searchMailbox,
      movePayment,
      rejectSuggestion,
      movingPaymentTo: movingPaymentTo[triageForContext.id] ?? null,
      rerunSuggestions,
      rerunningSuggestions,
      isProcessing,
      searching: searchingMailboxes[triageForContext.id] ?? null,
      searchFailure: searchFailures[triageForContext.id] ?? null,
      previewingEmailConnectionSearchResult,
      setPreviewingEmailConnectionSearchResult,
      reconcilingManually,
      setReconcilingManually: onReconcilingManuallyChange,
      importingEmailConnectionSearchResult:
        importingEmailConnectionSearchResults[triageForContext.id] ?? null,
      setImportingEmailConnectionSearchResult,
      isPreviewOpen: isPreviewOpen && !isOnWayToDocument,
      togglePreview,
      isOnWayToDocument,
      journalEntryIsBalanced,
      journalSettlement,
      bookLikeOffer,
      setBookLikeOffer,
      isSimilar,
      setIsSimilar: onSimilarChange,
      queueFilter,
      bulkBook,
      setBulkBook,
      bulkMatch,
      setBulkMatch,
      bulkPick,
      setBulkPick,
      recommendedTreatment,
      linedUp,
      setLinedUp,
      groupMenus,
      setGroupMenus,
      lineUpStatus,
      setLineUpStatus,
      lineUpRoom,
      setLineUpRoom,
      similarListElement,
      setSimilarListElement,
      registerCatalogChartedAccounts,
      registerCatalogDocuments,
      registerCatalogTransactions,
      registerStepSuggestions,
      isSearchFocused,
      expandedOptionKeys,
      setOptionExpanded,
    }),
    [
      triageForContext,
      selections,
      setSelection,
      activeOptionKey,
      activePreview,
      resolved,
      checkedCategoryOptionIds,
      toggleCategoryOptionChecked,
      suggestionsEngaged,
      onEngageSuggestions,
      isSuggestionsKeyboardActive,
      detailQueryLoading,
      isDetailReady,
      searchMailbox,
      movePayment,
      rejectSuggestion,
      movingPaymentTo,
      rerunSuggestions,
      rerunningSuggestions,
      isProcessing,
      searchFailures,
      searchingMailboxes,
      previewingEmailConnectionSearchResult,
      reconcilingManually,
      onReconcilingManuallyChange,
      importingEmailConnectionSearchResults,
      setImportingEmailConnectionSearchResult,
      isPreviewOpen,
      togglePreview,
      isOnWayToDocument,
      journalEntryIsBalanced,
      journalSettlement,
      bookLikeOffer,
      isSimilar,
      onSimilarChange,
      queueFilter,
      bulkBook,
      bulkMatch,
      bulkPick,
      recommendedTreatment,
      linedUp,
      groupMenus,
      lineUpStatus,
      lineUpRoom,
      similarListElement,
      registerCatalogChartedAccounts,
      registerCatalogDocuments,
      registerCatalogTransactions,
      registerStepSuggestions,
      isSearchFocused,
      expandedOptionKeys,
      setOptionExpanded,
    ],
  );

  const renderTabContent = () => (
    <>
      <TriageSuggestionsTabPanel
        suggestions={
          (triageForContext.suggestions ?? []) as TriageSuggestionStub[]
        }
        terms={searchTerms}
        // What is typed is looked for in the mailboxes as well - by the
        // Match card - until their chip is let go.
        searchesMailboxes={scopedGroupId !== "match" || !mailboxesDismissed}
      />
      <TriageReconcileManuallyDialog
        triage={triageForContext}
        entityIdentifiers={entityIdentifiers}
      />
    </>
  );

  useLayoutEffect(() => {
    if (!listItem) {
      onJournalResolvedChange(EMPTY_TRIAGE_JOURNAL_RESOLVED);
      return;
    }
    // Row reconcile chip: reflect changelist proposals and coding picks, not
    // manual document selection (documents tab / document options).
    onJournalResolvedChange({
      ...journalResolvedForBalance,
      documentOption: null,
      documentOptions: [],
    });
  }, [journalResolvedForBalance, listItem, onJournalResolvedChange]);

  const onEngageSuggestionsRef = useRef(onEngageSuggestions);
  onEngageSuggestionsRef.current = onEngageSuggestions;
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  const focusActiveOptionsList = useCallback(() => {
    onEngageSuggestionsRef.current();
  }, []);

  const enterFromList = useCallback(() => {
    // Highlight/focus only — do not commit a changelist (or other) selection.
    setActiveOptionKey(null);
    focusActiveOptionsList();
  }, [focusActiveOptionsList]);

  const { Scope: SuggestionsScope } = useContextScope({
    resetHotkeys: true,
    onEscape: () => onEscapeRef.current?.(),
    hotkeys: [
      {
        // Similar on or off - as from the queue.
        id: "triage-suggestions-book-like",
        combos: TRIAGE_KEYS.BOOK_LIKE,
        callback: () => similarRef.current.change?.(!similarRef.current.is),
      },
      {
        id: "triage-suggestions-accept-meta",
        combos: TRIAGE_KEYS.ACCEPT_META,
        // Think, on the kind of the card the keyboard is on while nothing is
        // picked to book - its menu in the toolbar says so - and otherwise
        // Book.
        callback: () => {
          const card = document.querySelector<HTMLElement>(
            '[data-testid="triage-suggestion-card"][data-active="true"][data-thinks-by-key]',
          );
          const group = card
            ? TRIAGE_SUGGESTION_GROUPS.find(
                (one) => one.id === card.dataset.group,
              )
            : undefined;
          if (group) rerunSuggestionsRef.current(group);
          else triggerAccept();
        },
      },
      {
        // The toolbar's menu of the kind of card the keyboard is on - as from
        // the queue, Match's where it is on none.
        id: "triage-suggestions-actions",
        combos: TRIAGE_KEYS.ACTIONS,
        callback: () => {
          const group =
            document.querySelector<HTMLElement>(
              '[data-testid="triage-suggestion-card"][data-active="true"]',
            )?.dataset.group ?? "match";
          const selector = `[data-testid="triage-group-actions"][data-group="${group}"]`;
          const menu = document.querySelector<HTMLElement>(selector);
          if (menu) {
            menu.click();
            return;
          }
          // The bar short of room, the kinds behind its "⋯": opened there.
          document
            .querySelector<HTMLElement>(
              '[data-testid="triage-group-menus-more"]',
            )
            ?.click();
          requestAnimationFrame(() =>
            document.querySelector<HTMLElement>(selector)?.click(),
          );
        },
      },
      {
        // The open payment's tags, on its row - as from the queue.
        id: "triage-suggestions-tags",
        combos: TRIAGE_KEYS.TAGS,
        callback: () => openTagsFromSuggestions(),
      },
      {
        // The bar's Hold, taking the keyboard from the suggestions.
        id: "triage-suggestions-hold",
        combos: TRIAGE_KEYS.HOLD,
        callback: () =>
          document
            .querySelector<HTMLElement>('[data-testid="triage-hold"]')
            ?.click(),
      },
      {
        // The one the keyboard is on, turned down.
        id: "triage-suggestions-reject",
        combos: TRIAGE_KEYS.REJECT,
        callback: () => rejectActiveRef.current(),
      },
      {
        // A document match the keyboard is on: its document, on its preview -
        // as its View does.
        id: "triage-suggestions-view-document",
        combos: TRIAGE_KEYS.VIEW_DOCUMENT,
        callback: () =>
          document
            .querySelector<HTMLElement>('[data-testid="triage-proposal-view"]')
            ?.click(),
      },
      {
        // A posting the keyboard is on: its category, to change - as a click
        // on its chip opens it.
        id: "triage-suggestions-category",
        combos: TRIAGE_KEYS.CATEGORY,
        callback: () =>
          document
            .querySelector<HTMLElement>(
              '[data-testid="triage-proposal-category"][data-active="true"][role="button"]',
            )
            ?.click(),
      },
      {
        // The payment's own Move, over its suggestions: the businesses to move
        // it to - as a click on it opens them. With nowhere to move it, there
        // is no Move.
        id: "triage-suggestions-move",
        combos: TRIAGE_KEYS.MOVE,
        // In Similar, where the payment has no Move of its own: how the move
        // card the keys are on settles them, its chip.
        callback: () =>
          document
            .querySelector<HTMLElement>(
              'button[data-testid="triage-payment-move"], [data-opens-by-key] button[data-slot="badge"]',
            )
            ?.click(),
      },
      {
        // Into the panel's search - narrowed to the card the keyboard is on,
        // its chip in it - to search.
        id: "triage-suggestions-hint",
        combos: "tab",
        callback: (event) => {
          const box = document.querySelector<HTMLElement>(
            "[data-testid='triage-suggestions-search'] input, input[data-testid='triage-suggestions-search']",
          );
          if (!box) return;
          event?.preventDefault();
          box.focus();
        },
      },
      {
        // The entry it would book, opened and shut from the suggestions - as
        // its button is, and not while that is shut.
        id: "triage-suggestions-preview",
        combos: TRIAGE_KEYS.PREVIEW,
        callback: () =>
          document
            .querySelector<HTMLElement>('[data-testid="triage-preview"]')
            ?.click(),
      },
      {
        // A draft in review the keyboard is on: its chat, to put it right
        // there first - as its Thread does.
        id: "triage-suggestions-draft-thread",
        combos: TRIAGE_KEYS.DRAFT_THREAD,
        callback: () =>
          document
            .querySelector<HTMLElement>('[data-testid="triage-draft-thread"]')
            ?.click(),
      },
      {
        // A draft in review, opened: confirmed where it is - as its Accept is.
        id: "triage-suggestions-accept-draft",
        combos: TRIAGE_KEYS.ACCEPT_DRAFT,
        callback: () =>
          document
            .querySelector<HTMLElement>('[data-testid="triage-draft-accept"]')
            ?.click(),
      },
      {
        // The stop the keyboard is on, opened under itself to what it was set
        // beside - not picked: Enter does that.
        id: "triage-suggestions-open",
        combos: "ArrowRight",
        callback: () => openActiveRef.current(true),
      },
      {
        // The one the keyboard is on, shut; already shut, back to the queue
        // beside the matches on a desktop - as a list's ← goes up a level.
        id: "triage-suggestions-back",
        combos: "ArrowLeft",
        callback: () => {
          if (!openActiveRef.current(false)) onEscapeRef.current?.();
        },
      },
    ],
  });

  useEffect(() => {
    if (!listItem) {
      registerEnterSuggestions(null);
      return;
    }
    registerEnterSuggestions(enterFromList);
    return () => registerEnterSuggestions(null);
  }, [enterFromList, listItem, registerEnterSuggestions]);

  // A payment's tags, saved on it as they are changed on its row - not left
  // for its booking, nor lost as another payment is opened - and the row
  // read so at once. Booked into another business, they are its row's there.
  const { mutate: updateNode } = useBackendMutation({
    mutation: Mutations.UPDATE_NODE,
  });
  const setTags = useCallback(
    (tags: TriageSelectedTag[]) => {
      setSelection("selectedTags", tags);
      const there = triageForContext.movedToTransaction;
      const payment = there?.id ?? triageForContext.id;
      const identifier = there?.identifier ?? triageForContext.identifier;
      if (!payment || !identifier) return;
      void updateNode({
        variables: {
          input: {
            id: btoa(`TransactionObject:${identifier}`),
            node: {
              transaction: {
                tags: {
                  nodes: tags.map((tag) => ({ identifier: tag.identifier })),
                },
              },
            },
          },
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({
              __typename: "TransactionObject",
              id: payment,
            }),
            fields: {
              tags: (_, { toReference }) =>
                tags.map((tag) =>
                  toReference({ __typename: "TagObject", id: tag.id }),
                ),
            },
          });
        },
      });
    },
    [
      setSelection,
      triageForContext.id,
      triageForContext.identifier,
      triageForContext.movedToTransaction,
      updateNode,
    ],
  );
  useEffect(() => {
    if (!listItem) {
      registerActiveRowStaging(null);
      return;
    }
    registerActiveRowStaging({
      triageIdentifier: listItem.identifier,
      selections,
      recommendedTreatment,
      movePayment,
      movingPaymentTo: movingPaymentTo[triageForContext.id] ?? null,
      isProcessing,
      isSimilar,
      setTags,
      triage: triageForContext,
    });
    return () => registerActiveRowStaging(null);
  }, [
    listItem,
    isProcessing,
    isSimilar,
    movePayment,
    movingPaymentTo,
    recommendedTreatment,
    registerActiveRowStaging,
    selections,
    setTags,
    triageForContext,
  ]);

  // One at a time the queue is behind the matches, so the payment they are for
  // stays in sight along the bottom, under them - its first two lines, whose
  // and what, flush with the screen's sides and inset as the cards above it.
  // It is one of a strip of the queue's payments, as a carousel's: drawn
  // across by a finger the strip follows it, the one before or after coming
  // into sight; let go far enough, it slides on and that one is the payment
  // (to the left the next, to the right the one before). With none that way,
  // it gives a little and springs back.
  const reducesMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const PAYMENT_SLIDE_MS = 220;
  const pinnedPaymentSlide = (
    item: TriageListItemFragment | null,
    testId?: string,
  ) => (
    <div
      data-testid={testId}
      // Its words where the cards' over it are: at the list's inset, as a
      // suggestion's card sets its own.
      className="flex-[0_0_100%] min-w-0 [border:1px_solid_transparent] pl-4 pr-4 pt-2.5 pb-2.5"
    >
      {item ? <TriageReconcileRow item={item} isCompact /> : null}
    </div>
  );
  const pinnedPayment =
    isOneAtATime && listItem ? (
      <div
        data-testid="triage-match-payment"
        onPointerDown={(event) => {
          if (event.pointerType === "mouse" || paymentSliding) return;
          setPaymentSnapped(false);
          paymentSwipeRef.current = {
            x: event.clientX,
            y: event.clientY,
            isSideways: false,
            dx: 0,
          };
        }}
        onPointerMove={(event) => {
          const swipe = paymentSwipeRef.current;
          if (!swipe) return;
          const dx = event.clientX - swipe.x;
          const dy = event.clientY - swipe.y;
          if (!swipe.isSideways) {
            if (Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) {
              paymentSwipeRef.current = null;
              return;
            }
            if (Math.abs(dx) <= 8) return;
            swipe.isSideways = true;
          }
          swipe.dx = dx;
          // Toward a side with no payment, it only gives a little.
          const beyond = dx < 0 ? !paymentAfter : !paymentBefore;
          setPaymentSwipeX(beyond ? dx / 4 : dx);
        }}
        onPointerUp={() => {
          const swipe = paymentSwipeRef.current;
          paymentSwipeRef.current = null;
          setPaymentSwipeX(0);
          // How far it came, as the finger said - not as last drawn.
          if (!swipe?.isSideways || Math.abs(swipe.dx) <= 56) return;
          const direction = swipe.dx < 0 ? 1 : -1;
          if (!(direction === 1 ? paymentAfter : paymentBefore)) return;
          setPaymentSliding(direction);
          // Once in, it is the payment: drawn in the middle, not slid there.
          window.setTimeout(
            () => {
              onSwipeToPayment?.(direction);
              setPaymentSnapped(true);
              setPaymentSliding(0);
            },
            reducesMotion ? 0 : PAYMENT_SLIDE_MS,
          );
        }}
        onPointerCancel={() => {
          paymentSwipeRef.current = null;
          setPaymentSwipeX(0);
        }}
        // Up and down is the page's; across is the payment's.
        className="[border-top-width:1px] [border-top-style:solid] border-border overflow-hidden [touch-action:pan-y]"
      >
        <div
          data-testid="triage-match-payment-strip"
          // Following the finger, at once; let go, on or back in a moment -
          // or at once, where motion is turned down.
          className="flex transform-(--strip-transform) [transition:var(--strip-transition)] motion-reduce:[transition:none]"
          style={
            {
              "--strip-transform": `translateX(calc(${-100 * (1 + paymentSliding)}% + ${paymentSwipeX}px))`,
              "--strip-transition":
                paymentSwipeX !== 0 || paymentSnapped
                  ? "none"
                  : `transform ${PAYMENT_SLIDE_MS}ms cubic-bezier(0.33, 1, 0.68, 1)`,
            } as CSSProperties
          }
        >
          {pinnedPaymentSlide(paymentBefore)}
          {pinnedPaymentSlide(listItem, "triage-match-payment-current")}
          {pinnedPaymentSlide(paymentAfter)}
        </div>
      </div>
    ) : null;

  // Similar on: the payment pinned over its payee's others in the queue's
  // place, ticked, each saying what it becomes - ↑ ↓ about them, Space or a
  // click ticks one, → into the bulk list, Escape back to the queue.
  const [isBallsShown, setIsBallsShown] = useState(false);
  // Shown once the rest have folded away and its payments are read - till
  // then the queue, narrowed to the payment and still on it: never a list of
  // placeholders in the payment's place.
  const readBookLike =
    bookLikeOffer?.bookLike.isLoading === false ? bookLikeOffer.bookLike : null;
  const isSimilarShown =
    (isSimilar || similarClosing === "balls") &&
    isSimilarListed &&
    readBookLike != null;
  // Off, and its list gone, its keys on none: a list of them elsewhere (a
  // Move's) holds none in sight till its keys are put on one - and Similar's
  // own list is not drawn again for it as it goes.
  const setBookLikeKeyed = bookLikeOffer?.bookLike.setKeyed;
  useEffect(() => {
    if (!isSimilar && !isSimilarShown) setBookLikeKeyed?.(null);
  }, [isSimilar, isSimilarShown, setBookLikeKeyed]);
  // Turned off from a line-up, the list keeps what it was lined up with till
  // it has gone: drawn again without it, every row under one moved up, and
  // then down again as the queue came back round them.
  const liningRef = useRef({ bulkMatch, linedUp, lineUpRoom });
  if (isSimilar) liningRef.current = { bulkMatch, linedUp, lineUpRoom };
  const lining = liningRef.current;
  const similarList =
    isSimilarShown && readBookLike ? (
      <div
        // What a match lines up beside.
        ref={setSimilarListElement}
        data-testid="triage-similar"
        // Its balls: in a frame after it is shown, so they fade in; out as it
        // is turned off - by their style, its rows not drawn again. Turned
        // off, out in the same render, so they have all their time to fade
        // before the list goes.
        data-balls-hidden={!(isBallsShown && isSimilar) || undefined}
        onKeyDown={(event) => {
          // Its own keys - not those of a menu opened from it, drawn
          // elsewhere on the page.
          if (!event.currentTarget.contains(event.target as Node)) return;
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            // A match picked: back to the bulk list first.
            if (bulkMatch) setBulkMatch(null);
            else onSimilarChange?.(false);
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            event.stopPropagation();
            onEngageSuggestions();
          }
        }}
        className="flex-1 min-h-0 flex flex-col"
      >
        <TriageBookLikeCards
          bookLike={readBookLike}
          fill
          isSource
          onOpenPanel={null}
          // The queue's bar over it, as it is over the queue: its own
          // filters are in that bar's Filter.
          hasFilterBar={false}
          // A match picked: each card lined up across two businesses draws
          // the other's half.
          lining={lining.bulkMatch}
          linedUp={lining.bulkMatch ? lining.linedUp : null}
          // The bulk list's row picked: what is staged on them.
          pick={isSimilar ? bulkPick : null}
          // What is unplaced beside its foot: room to scroll on to it.
          roomBelow={lining.bulkMatch ? lining.lineUpRoom : 0}
        />
      </div>
    ) : null;
  useEffect(() => {
    if (!(isSimilar && isSimilarShown)) {
      setIsBallsShown(false);
      return;
    }
    const frame = requestAnimationFrame(() => setIsBallsShown(true));
    return () => cancelAnimationFrame(frame);
  }, [isSimilar, isSimilarShown]);
  // Shown, the keys on it: the queue they were on is out of sight.
  useEffect(() => {
    if (!isSimilarShown || !similarListElement) return;
    const frame = requestAnimationFrame(() =>
      similarListElement
        .querySelector<HTMLElement>('[data-testid="triage-book-like-list"]')
        ?.focus(),
    );
    return () => cancelAnimationFrame(frame);
  }, [isSimilarShown, similarListElement]);

  /**
   * *bar*: what the panel's bar holds of the payment - its controls held in
   * place while it loads, there with nothing to do while none is picked.
   */
  const renderWithProvider = (
    detailPanel: ReactNode | null,
    bar: "loading" | "empty" | "ready" | null,
  ) => {
    // Provider wraps only the Match panel — not the list. Otherwise every
    // detail/context rebuild re-renders all rows.
    const providedDetail = detailPanel ? (
      <TriageDetailContext.Provider value={detailContextValue}>
        {detailPanel}
      </TriageDetailContext.Provider>
    ) : null;
    return (
      <>
        {/* The queue and what the selected payment is matched to, side by
            side in the view's own content - not the node panel, which this
            view turns off - each keeping its share as the content widens
            or narrows. */}
        {/* The open payment and the suggestion picked for it, joined -
            side by side, where both are in sight. */}
        {isOneAtATime ? null : <TriageLinks />}
        <AppLayoutContentAreas
          id="triage-content"
          shown={isMatchShown ? "triage-match" : "triage-queue"}
          isOneAtATime={isOneAtATime}
        >
          <AppLayoutContentArea
            id="triage-queue"
            defaultSize="45%"
            minSize={TRIAGE_QUEUE_MIN_WIDTH}
            toolbar={queueToolbar?.(
              // Similar on: its own filters, in the queue's bar's Filter.
              isSimilarShown && similarBookLike
                ? {
                    filters: similarBookLike.quickFilters.map(
                      (chip): TriageLikeFilter => ({
                        key: chip.key,
                        label: chip.value,
                        kind: chip.name,
                        isOn: chip.isOn,
                      }),
                    ),
                    onToggle: (key) => {
                      const chip = similarBookLike.quickFilters.find(
                        (one) => one.key === key,
                      );
                      if (chip) similarBookLike.toggleFilter(key, chip.value);
                    },
                    onClear: similarBookLike.clearFilters,
                  }
                : null,
            )}
          >
            {layoutContent ? layoutContent(similarList, isSimilarShown) : null}
          </AppLayoutContentArea>
          <AppLayoutContentArea
            id="triage-match"
            defaultSize="55%"
            minSize={TRIAGE_MATCH_MIN_WIDTH}
            // The panel's search and tabs, over it whatever it shows - the
            // queue loading, a payment's detail loading, nothing picked -
            // and usable once there is a payment's detail to search.
            // Its one row: its search, a field taking what the rest leaves
            // it, then what is done with the suggestions and the payment.
            toolbar={
              <div
                ref={setMatchBar}
                data-testid="triage-match-bar"
                className="flex-1 min-w-0 flex items-center pl-2 pr-2 gap-3"
              >
                {/* One at a time the queue is behind the matches, not
                    beside them. */}
                {isOneAtATime && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Back to the queue"
                    onClick={onShowQueue}
                    className="-mr-1"
                  >
                    <Icon ReactIcon={icons.chevronLeft} size="md" />
                  </Button>
                )}
                <TriageResolutionSearchField
                  // What the search looks through: the card its chip names,
                  // the mailboxes too where they are looked in.
                  placeholder={`Search for ${
                    isSimilar
                      ? (bulkMatch ?? "anything")
                      : scopedMailboxes?.length
                        ? "documents and emails"
                        : (scopedGroup?.searches ?? "anything")
                  }…`}
                  value={resolutionSearchText}
                  onChange={setResolutionSearchText}
                  disabled={!isDetailReady}
                  // Similar on, the match picked in its bulk list - its x back
                  // to the list.
                  scope={
                    isSimilar
                      ? bulkMatch
                        ? BULK_MATCH_SCOPES[bulkMatch]
                        : null
                      : scopedGroup
                        ? {
                            title: scopedGroup.title,
                            icon: JOB_ICONS[
                              scopedGroup.jobs[0] as TriageJobEnum
                            ],
                          }
                        : null
                  }
                  onClearScope={() =>
                    isSimilar ? setBulkMatch(null) : setScopedGroupId(null)
                  }
                  onStep={(by) => stepSuggestionsRef.current(by)}
                  mailboxes={isSimilar ? null : scopedMailboxes}
                  onClearMailboxes={() => setMailboxesDismissed(true)}
                  onFocusChange={(isFocused) => {
                    setIsSearchFocused(isFocused);
                    onSuggestionsSearchFocus?.(isFocused);
                  }}
                  // Tab comes into it from the suggestions, and from the
                  // queue's search - and goes back to that.
                  canTabIn={suggestionsEngaged || searchFocus === "queue"}
                  onTabOut={() =>
                    document
                      .querySelector<HTMLInputElement>(
                        '[data-testid="triage-queue-search"]',
                      )
                      ?.focus()
                  }
                  terms={barTerms}
                  offer={offeredTerm}
                  onAddTerm={(term) => {
                    setEnteredTerms((current) => [
                      ...current.filter((one) => one !== term),
                      term,
                    ]);
                    setSearchTerms((current) => ({
                      ...current,
                      [barTermsKey]: [...(current[barTermsKey] ?? []), term],
                    }));
                    setResolutionSearchText("");
                  }}
                  onRemoveTerm={(index) =>
                    setSearchTerms((current) => ({
                      ...current,
                      [barTermsKey]: (current[barTermsKey] ?? []).filter(
                        (_, at) => at !== index,
                      ),
                    }))
                  }
                  isSearchingMailboxes={
                    searchingMailboxes[triageForContext.id]?.query != null
                  }
                  inputTestId="triage-suggestions-search"
                  containerClassName="flex-1 min-w-30"
                />
                {bar ? (
                  <TriageDetailContext.Provider value={detailContextValue}>
                    <TriageSuggestionsToolbar
                      isLoading={bar === "loading"}
                      isEmpty={bar === "empty"}
                      isFolded={isMatchBarFolded}
                    />
                  </TriageDetailContext.Provider>
                ) : null}
              </div>
            }
          >
            {providedDetail ?? (
              // Nothing picked: its bar all the same, nothing on it to do.
              <div className="flex-1 min-h-0 flex flex-col">
                <EmptyState
                  ReactIcon={InboxIcon}
                  title="Select a payment"
                  description="What it could be matched to shows here."
                />
              </div>
            )}
          </AppLayoutContentArea>
        </AppLayoutContentAreas>
        {/* One at a time, the payment the matches are for, along the
            bottom under them - the layout's footer, the matches' alone. What
            is done to the payment is on the suggestions' bar. */}
        {isMatchShown && pinnedPayment ? (
          <AppLayoutFooter>{pinnedPayment}</AppLayoutFooter>
        ) : null}
      </>
    );
  };

  if (!listItem) {
    // The queue loading, a spinner stands in for what a payment will show -
    // under the detail's provider, as a payment's own detail is.
    return renderWithProvider(
      queueLoading ? <TriageMatchPanelShell isLoading /> : null,
      queueLoading ? "loading" : "empty",
    );
  }

  if ((detailQueryLoading && !isDetailReady) || (!error && isCardsLoading)) {
    return renderWithProvider(<TriageMatchPanelShell isLoading />, "loading");
  }

  if (!detailQueryLoading && (error || !isDetailReady)) {
    const errorPanel = (
      <SuggestionsScope
        tabIndex={-1}
        data-testid="triage-suggestions-scope"
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <TriageMatchPanelShell
          scrollable={
            <div className="flex-1 flex items-center justify-center">
              <Typography
                level="body-md"
                className="text-muted-foreground/80 text-center"
              >
                {error
                  ? "Couldn't load triage details."
                  : "This transaction is no longer in the triage queue."}
              </Typography>
            </div>
          }
        />
      </SuggestionsScope>
    );

    return renderWithProvider(errorPanel, null);
  }

  const detailPanel = (
    <SuggestionsScope
      tabIndex={-1}
      data-testid="triage-suggestions-scope"
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        outline: "none",
      }}
    >
      {/* What its Preview opens, under the bar it is on. */}
      <TriagePreview
        triage={journalSource}
        resolved={journalResolvedForBalance}
        onRemoveLineSource={removeJournalLineSource}
      />
      <TriageHoldNotice />
      {/* Similar on, the bulk list of what is done to them all, in the
          suggestions' place. */}
      {isSimilar ? (
        <TriageBookLikeChanges terms={searchTerms} />
      ) : (
        <TriageMatchPanelShell scrollable={renderTabContent()} />
      )}
    </SuggestionsScope>
  );

  return renderWithProvider(detailPanel, "ready");
}

/** Reconcile card height (inter-row spacing comes from list ``gap``). */
const TRIAGE_LIST_ROW_HEIGHT = 104;
/** How many of the queue's rows past those on screen, either side, come back first as Similar closes. */
const TRIAGE_QUEUE_NEAR_ROWS = 3;

/**
 * Rows fetched per page. The queue is unbounded and every row carries its
 * suggestions, so fetching it whole makes the first paint scale with the
 * backlog; the list pages in the rest on scroll.
 */
const TRIAGE_PAGE_SIZE = 25;

function TriageList({
  items,
  listKey,
  loading,
  selectedId,
  onSelect,
  onNavigate,
  onPrefetch,
  isKeyboardActive,
  loadMore,
  activeRowStaging,
  isFillingIn,
}: {
  items: TriageListItemFragment[];
  listKey: string;
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNavigate: (id: string) => void;
  onPrefetch: (identifier: string) => void;
  isKeyboardActive?: () => boolean;
  loadMore?: SelectableCardListLoadMore;
  activeRowStaging: TriageActiveRowStaging | null;
  /** The rest of the queue coming back round those drawn as Similar closed: put in where they are, nothing on screen moved. */
  isFillingIn: boolean;
}) {
  const { holdTriage, takeOffHold } = useTriageViewActions();
  return (
    <SelectableCardList
      items={items}
      isFillingIn={isFillingIn}
      // On a touch screen a payment is held, or let go, by swiping it - as
      // the bar's Hold does, without opening it. One waiting on the viewer's
      // answer about a receipt is answered in its notice instead.
      getItemSwipeActions={(item) =>
        !item.triageHeldAt
          ? [
              {
                key: "hold",
                label: "Hold",
                icon: <PauseIcon size="1em" />,
                onAction: () => holdTriage(item.id),
              },
            ]
          : item.triageHeldForViewer || !takeOffHold
            ? null
            : [
                {
                  key: "unhold",
                  label: "Unhold",
                  icon: <PlayIcon size="1em" />,
                  onAction: () => takeOffHold(item.id),
                },
              ]
      }
      loading={loading}
      loadMore={loadMore}
      loadingItemCount={TRIAGE_RECONCILE_ROW_SKELETON_COUNT}
      estimatedItemHeight={TRIAGE_LIST_ROW_HEIGHT}
      variableItemHeight
      disableVirtualization
      // Booked, moved or held off, a payment folds away - one another search
      // or filter leaves out goes at once.
      animatesRemovals
      listKey={listKey}
      size="md"
      variant="outlined"
      layout="cards"
      // A long queue under a moving pointer: only the title answers it.
      rowHover="title"
      getItemKey={(item) => item.id}
      isItemActive={(item) => selectedId === item.id}
      onItemClick={(item) => {
        onSelect(item.id);
      }}
      onItemNavigate={(item) => {
        onNavigate(item.id);
      }}
      onItemHover={(item) => {
        onPrefetch(item.identifier);
      }}
      scrollToActiveKey={selectedId}
      stickyActiveRow
      isKeyboardActive={isKeyboardActive}
      data-testid="triage-list"
      className="flex flex-col"
      emptyState={{
        ReactIcon: InboxIcon,
        title: "All caught up",
        description: "No unclassified transactions remaining.",
      }}
      placeholderItem={TRIAGE_RECONCILE_ROW_PLACEHOLDER}
      renderItem={({ item, isActive, isLoading }) => (
        // A name on a payment not picked picks it, as anywhere on it does.
        <TriageRowPickedContext.Provider value={isActive}>
          <TriageReconcileRow
            item={item}
            isActive={isActive}
            isLoading={isLoading}
            rowStaging={
              isActive && activeRowStaging?.triageIdentifier === item.identifier
                ? activeRowStaging
                : null
            }
          />
        </TriageRowPickedContext.Provider>
      )}
    />
  );
}

function TriageWorkbench({
  panelRef,
  items,
  listKey,
  loading,
  selectedId,
  onSelect,
  onNavigate,
  onPrefetch,
  isTriageListKeyboardActive,
  loadMore,
  activeRowStaging,
  isFillingIn,
}: {
  panelRef: React.RefObject<HTMLDivElement | null>;
  items: TriageListItemFragment[];
  listKey: string;
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNavigate: (id: string) => void;
  onPrefetch: (identifier: string) => void;
  isTriageListKeyboardActive: () => boolean;
  loadMore?: SelectableCardListLoadMore;
  activeRowStaging: TriageActiveRowStaging | null;
  isFillingIn: boolean;
}) {
  return (
    <div ref={panelRef} className="h-full w-full flex flex-col overflow-hidden">
      <TriageList
        items={items}
        listKey={listKey}
        loading={loading}
        selectedId={selectedId}
        onSelect={onSelect}
        onNavigate={onNavigate}
        onPrefetch={onPrefetch}
        isKeyboardActive={isTriageListKeyboardActive}
        loadMore={loadMore}
        activeRowStaging={activeRowStaging}
        isFillingIn={isFillingIn}
      />
    </div>
  );
}

/** How long the pointer rests on a row before its detail is asked for ahead of a click. */
const TRIAGE_PREFETCH_REST_MS = 500;

/**
 * The keys that move about triage - from payment to payment, option to option,
 * opening and shutting - and how soon after one another they are too quick
 * for what opens and shuts to slide: the longest slide here
 * (``TriageDocumentPairs``' glide).
 */

const TRIAGE_MOVING_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "PageUp",
  "PageDown",
  "Tab",
  "Enter",
  "Escape",
  "j",
  "k",
  // A payment carried up or down where it is lined up with its documents.
  "s",
  "d",
]);
const TRIAGE_RUSH_MS = 320;

export function TriageView() {
  const apolloClient = useApolloClient();
  // Moved about faster than what opens and shuts can slide - or a key held -
  // each change is drawn at once: no slide begun over one still running, and
  // so nothing pushed about as they fight. The keys let be as long as a slide
  // takes, they slide again.
  useEffect(() => {
    let last = -Infinity;
    let settling: number | undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!TRIAGE_MOVING_KEYS.has(event.key)) return;
      const now = performance.now();
      if (event.repeat || now - last < TRIAGE_RUSH_MS) {
        MotionGlobalConfig.skipAnimations = true;
      }
      last = now;
      window.clearTimeout(settling);
      settling = window.setTimeout(() => {
        MotionGlobalConfig.skipAnimations = false;
      }, TRIAGE_RUSH_MS);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.clearTimeout(settling);
      MotionGlobalConfig.skipAnimations = false;
    };
  }, []);
  const [selectedTriageId, setSelectedTriageId] = useState<string | null>(null);
  // Similar on - the chip in the queue's search, or L - for the payment it
  // was turned on for: its payee's others in the queue's place, its
  // suggestions the bulk list. Another payment picked, it is off.
  const [similarOf, setSimilarOf] = useState<string | null>(null);
  const isSimilar = similarOf != null && similarOf === selectedTriageId;
  const isSimilarRef = useRef(isSimilar);
  isSimilarRef.current = isSimilar;
  // The queue and a payment's matches one at a time - on a phone's screen or
  // a window as narrow, or where triage itself is narrower than both need
  // side by side, the chat open beside it on a laptop. A computer keeps what a
  // computer has there: its keys, their hints and its pickers.
  const { isMobile, isNarrow } = useDevice();
  // The queue and the payment open one at a time - a payment clicked into,
  // and back - as the person chose beside the queue's filters, and remembered.
  const prefersOneAtATime = State((state) => state.isTriageOneAtATime);
  const [isTooNarrow, setIsTooNarrow] = useState(false);
  const measuredRef = useResizeObserver((rect) =>
    setIsTooNarrow(
      (was) =>
        rect.width <
        TRIAGE_SIDE_BY_SIDE_MIN_WIDTH + (was ? TRIAGE_SIDE_BY_SIDE_SLACK : 0),
    ),
  );
  const isOneAtATime = isNarrow || isTooNarrow || prefersOneAtATime;
  // One at a time, a payment picked shows its matches, until the way back to
  // the queue is taken. Picked for the person rather than by them - the
  // first, or the next after one is accepted - does not take them from where
  // they are.
  const [isMatchShown, setIsMatchShown] = useState(false);
  const [reconcilingManually, setReconcilingManually] =
    useState<TriageResolutionTab | null>(null);
  const [journalPreviewResolved, setJournalPreviewResolved] =
    useState<TriageJournalResolved>(EMPTY_TRIAGE_JOURNAL_RESOLVED);

  const handleJournalResolvedChange = useCallback(
    (resolved: TriageJournalResolved) => {
      setJournalPreviewResolved((current) => {
        if (current === resolved) {
          return current;
        }
        if (
          current.linesSuggestion === resolved.linesSuggestion &&
          current.chartedAccountOption === resolved.chartedAccountOption &&
          current.categoryOption === resolved.categoryOption &&
          current.documentOption === resolved.documentOption &&
          current.documentOptions === resolved.documentOptions &&
          current.selectedTransactions === resolved.selectedTransactions
        ) {
          return current;
        }
        return resolved;
      });
    },
    [],
  );

  const activeEntityIdentifiers = State(
    (state) => state.activeEntityIdentifiers,
  );

  // Each answers with the payment's triage row as it now stands, which Apollo
  // writes over the one the page shows: nothing needs reading again.
  const { mutate: holdTriageTransaction } = useBackendMutation({
    mutation: Mutations.HOLD_TRIAGE_TRANSACTION,
  });
  const { mutate: unholdTriageTransaction } = useBackendMutation({
    mutation: Mutations.UNHOLD_TRIAGE_TRANSACTION,
  });

  const acceptHandlerRef = useRef<(() => void) | null>(null);
  const registerAccept = useCallback((handler: (() => void) | null) => {
    acceptHandlerRef.current = handler;
  }, []);

  // The open payment's tags, on its row: what X opens.
  const openTagsRef = useRef<(() => void) | null>(null);
  const registerOpenTags = useCallback((handler: (() => void) | null) => {
    openTagsRef.current = handler;
  }, []);
  const openTags = useCallback(() => openTagsRef.current?.(), []);

  const enterSuggestionsRef = useRef<(() => void) | null>(null);
  const registerEnterSuggestions = useCallback(
    (handler: (() => void) | null) => {
      enterSuggestionsRef.current = handler;
    },
    [],
  );

  const activeRowStagingRef = useRef<TriageActiveRowStaging | null>(null);
  const [activeRowStaging, setActiveRowStaging] =
    useState<TriageActiveRowStaging | null>(null);
  const registerActiveRowStaging = useCallback(
    (staging: TriageActiveRowStaging | null) => {
      activeRowStagingRef.current = staging;
      setActiveRowStaging(staging);
    },
    [],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilterTerm, setSearchFilterTerm] = useState("");
  // Which search the keyboard is in: Tab goes from either to the other, said
  // in the other as it waits.
  const [searchFocus, setSearchFocus] = useState<
    "queue" | "suggestions" | null
  >(null);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchFilterTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // The queues picked in the toolbar's filter: payments in every one picked
  // in, and in none picked out.
  const [queueFilters, setQueueFilters] = useState<TriageQueuePicks>({});

  const triageFilter = useMemo((): TransactionFilter | undefined => {
    const parts: TransactionFilter[] = [];
    if (searchFilterTerm) {
      const byWords: TransactionFilter = {
        field: { description: { iContains: searchFilterTerm } },
      };
      // Typed as an amount - "$1,010.15", "(10.15)", "-12" - it is also the
      // payments of exactly that, out or in: the sign, the dollar, commas and
      // spaces said as people say them, and nothing else.
      const figure = searchFilterTerm
        .replace(/[\s$,()]/g, "")
        .replace(/^-/, "");
      const amount = /^\d+(\.\d{1,2})?$/.test(figure) ? Number(figure) : null;
      parts.push(
        amount != null
          ? {
              any: [
                byWords,
                { field: { amount: { inList: [amount, -amount] } } },
              ],
            }
          : byWords,
      );
    }
    const queues = Object.entries(queueFilters) as [TriageQueueEnum, boolean][];
    const inQueues = queues.filter(([, isIn]) => isIn).map(([queue]) => queue);
    const outOfQueues = queues
      .filter(([, isIn]) => !isIn)
      .map(([queue]) => queue);
    if (inQueues.length > 0) {
      parts.push({ triageQueue: inQueues });
    }
    if (outOfQueues.length > 0) {
      parts.push({ notInTriageQueue: outOfQueues });
    }
    return parts.length === 0
      ? undefined
      : parts.length === 1
        ? parts[0]
        : { all: parts };
  }, [queueFilters, searchFilterTerm]);
  // Asked for the payments missing a document, those booked are listed too:
  // there for their document alone - or asked for the booked, to look over or
  // move to another business.
  const includesBooked =
    queueFilters[TriageQueueEnum.MissingDocument] === true ||
    queueFilters[TriageQueueEnum.Booked] === true;

  // What the queue lists: another search, filter or business is another list.
  const triageListKey = JSON.stringify([
    activeEntityIdentifiers,
    triageFilter,
    includesBooked,
  ]);
  const { data, loading, networkStatus, fetchMore } = useBackendQuery(
    Queries.TRIAGE_CONNECTION_QUERY,
    {
      variables: {
        entityIdentifiers: activeEntityIdentifiers,
        first: TRIAGE_PAGE_SIZE,
        filter: triageFilter,
        includesBooked,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
  );

  // Payments on their way out - booked with the one booked, their own booking
  // not yet answered - kept out of the queue though it is read again
  // meanwhile (``holdOffQueue``).
  const [heldOff, setHeldOff] = useState<ReadonlySet<string>>(new Set());
  const triageItems: TriageListItemFragment[] = useMemo(
    () =>
      (
        data?.triageConnection?.data?.edges?.map((edge) => edge.node) ?? []
      ).filter((item) => !heldOff.has(item.identifier)),
    [data, heldOff],
  );

  // Similar on, the payee's others in the queue - the rest folding away
  // before Similar's list takes its place (``TriageDetail``).
  const [similarRows, setSimilarRows] = useState<ReadonlySet<string> | null>(
    null,
  );
  // Where the payment stood on screen in the queue as Similar was turned on is
  // where it is put back once Similar is off - L, then L again, leaves the
  // queue as it was; a payment that had to slide to the nearest place
  // Similar's list had for it slides back. Where it would stand unheld, as
  // one held at the top or the foot stands anywhere.
  const measureQueueRow = useCallback(() => {
    const queue = scopeRef.current?.querySelector<HTMLElement>(
      '[data-testid="triage-list"]',
    );
    const scroller = queue?.matches("[data-selectable-card-list]")
      ? queue
      : queue?.querySelector<HTMLElement>("[data-selectable-card-list]");
    const row = scroller?.querySelector<HTMLElement>(
      "[data-selectable-row-active]:not([data-motion-pop-id])",
    );
    if (!scroller) return null;
    // Not in the queue - left out by its search or filters - only the list.
    if (!row) return { scroller, top: null };
    const position = row.style.position;
    row.style.position = "static";
    const top = row.offsetTop;
    row.style.position = position;
    return { scroller, top };
  }, []);
  const queueRowTopRef = useRef<number | null>(null);
  const queueSeenRef = useRef<(string | null | undefined)[]>([]);
  // Similar off, the queue's rows from the payment to those that were on
  // screen come back first, a few either side, and the rest, out of sight,
  // once those have glided in: putting back all of a long queue at once took
  // the glide's first frames. The list holds the payment where it is as they
  // come, and is scrolled back as far past it as it was.
  const [nearRows, setNearRows] = useState<ReadonlySet<string> | null>(null);
  const [narrowedTo, setNarrowedTo] = useState<ReadonlySet<string> | null>(
    null,
  );
  // Where the narrowed queue was scrolled as the rest come back: rows held
  // where Similar's list showed them are held there however it is scrolled
  // as they come.
  const narrowedScrollRef = useRef<number | null>(null);
  if (similarRows !== narrowedTo) {
    setNarrowedTo(similarRows);
    if (similarRows == null && narrowedTo != null) {
      narrowedScrollRef.current = measureQueueRow()?.scroller.scrollTop ?? null;
      const at = [selectedTriageId, ...queueSeenRef.current]
        .map((id) => triageItems.findIndex((item) => item.id === id))
        .filter((index) => index >= 0);
      setNearRows(
        new Set([
          ...narrowedTo,
          ...triageItems
            .slice(
              Math.max(0, Math.min(...at) - TRIAGE_QUEUE_NEAR_ROWS),
              Math.max(...at) + TRIAGE_QUEUE_NEAR_ROWS + 1,
            )
            .map((item) => item.identifier),
        ]),
      );
    }
  }
  // The rest come back filling in round those drawn: put in where they are,
  // the list scrolled by as far as they move the rest - nothing on screen
  // glides for them (``isFillingIn``), only for that once.
  const [isQueueFillingIn, setIsQueueFillingIn] = useState(false);
  useEffect(() => {
    if (!nearRows) return;
    const timeout = setTimeout(
      () =>
        startTransition(() => {
          setNearRows(null);
          setIsQueueFillingIn(true);
        }),
      SELECTABLE_CARD_LIST_FOLD_MS,
    );
    return () => clearTimeout(timeout);
  }, [nearRows]);
  useEffect(() => {
    if (isQueueFillingIn) setIsQueueFillingIn(false);
  }, [isQueueFillingIn]);
  const queueItems = useMemo(() => {
    const shown = similarRows ?? nearRows;
    return shown
      ? triageItems.filter((item) => shown.has(item.identifier))
      : triageItems;
  }, [nearRows, similarRows, triageItems]);
  // Back where it was as Similar opened, in the moment its rows come back,
  // so they glide to where they were.
  useLayoutEffect(() => {
    if (similarRows != null) return;
    const at = measureQueueRow();
    const wasTop = queueRowTopRef.current;
    queueRowTopRef.current = null;
    if (at?.top != null && wasTop != null) {
      at.scroller.scrollTop = at.top - wasTop;
    }
    // And its rows, held where Similar's list showed them (``TriageDetail``),
    // let go with the glides of those coming back.
    // Let go whether or not the payment was found to be put back where it
    // was: held, they would wait for the fallback and slide again after all
    // else had settled.
    const scrolled =
      at && narrowedScrollRef.current != null
        ? at.scroller.scrollTop - narrowedScrollRef.current
        : 0;
    for (const slide of at?.scroller.getAnimations({ subtree: true }) ?? []) {
      if (slide.playState !== "paused") continue;
      const effect = slide.effect as KeyframeEffect | null;
      const by = Number.parseFloat(
        String(effect?.getKeyframes()[0]?.transform ?? "").replace(
          /^translateY\(/,
          "",
        ),
      );
      if (scrolled && Number.isFinite(by)) {
        effect?.setKeyframes([
          { transform: `translateY(${by + scrolled}px)` },
          { transform: "translateY(0px)" },
        ]);
      }
      // From the frame after the one these rows are drawn in, where framer's
      // glides start: played now, a slide's clock starts with this frame, and
      // the longer it took, the further the slide ran ahead of the glide it
      // adds to - a row went the slide's way first, then the glide's.
      requestAnimationFrame(() =>
        requestAnimationFrame((time) => {
          if (slide.playState === "paused") slide.startTime = time;
        }),
      );
    }
  }, [measureQueueRow, similarRows]);

  const pageInfo = data?.triageConnection?.data?.pageInfo;
  const totalCount = data?.triageConnection?.data?.totalCount;
  const readCount = data?.triageConnection?.data?.edges?.length ?? 0;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const loadMoreTriage = useCallback(() => {
    if (isFetchingMore || !pageInfo?.hasNextPage) {
      return;
    }
    void fetchMore({
      variables: {
        entityIdentifiers: activeEntityIdentifiers,
        first: TRIAGE_PAGE_SIZE,
        after: pageInfo.endCursor,
        filter: triageFilter,
        includesBooked,
      },
    });
  }, [
    activeEntityIdentifiers,
    fetchMore,
    includesBooked,
    isFetchingMore,
    pageInfo?.endCursor,
    pageInfo?.hasNextPage,
    triageFilter,
  ]);

  const triageLoadMore = useMemo(
    () =>
      pageInfo?.hasNextPage
        ? {
            hasMore: true,
            loading: isFetchingMore,
            onLoadMore: loadMoreTriage,
            // As many placeholder rows as the next page brings.
            loadingItemCount:
              totalCount == null
                ? undefined
                : Math.max(
                    1,
                    Math.min(TRIAGE_PAGE_SIZE, totalCount - readCount),
                  ),
          }
        : undefined,
    [
      isFetchingMore,
      loadMoreTriage,
      pageInfo?.hasNextPage,
      readCount,
      totalCount,
    ],
  );

  // A payment the AI has finished with is read again, so its row's chips and
  // its detail show what it proposed. The push that clears the row's mark
  // carries the row, not its suggestions.
  const processingIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const processing = new Set(
      triageItems.filter((item) => item.isProcessing).map((item) => item.id),
    );
    const listed = new Set(triageItems.map((item) => item.id));
    for (const id of processingIdsRef.current) {
      if (!processing.has(id) && listed.has(id)) {
        void apolloClient.query({
          query: Queries.TRIAGE_DETAIL_QUERY,
          variables: { id },
          fetchPolicy: "network-only",
        });
      }
    }
    processingIdsRef.current = processing;
  }, [apolloClient, triageItems]);

  const triageItemsRef = useRef(triageItems);
  triageItemsRef.current = triageItems;
  const selectedIdRef = useRef(selectedTriageId);
  selectedIdRef.current = selectedTriageId;

  const hasAutoSelected = useRef(false);
  if (!hasAutoSelected.current && triageItems.length > 0 && !selectedTriageId) {
    hasAutoSelected.current = true;
    setSelectedTriageId(triageItems[0].id);
  }

  // Warm the detail of a payment about to be opened, so a click renders it
  // whole rather than from the list's stub - and nothing more: its drafts, a
  // search of the mailboxes and its payee's others are asked for by its own
  // panel once it is open. Asked ahead for every row in passing, those were
  // most of what the server did, and a payment opened waited behind them.
  const prefetchTriageDetail = useCallback(
    (identifier: string) => {
      if (!identifier) {
        return;
      }
      apolloClient
        .query({
          query: Queries.TRIAGE_DETAIL_QUERY,
          variables: {
            id: identifierAndTypenameToId(identifier, "TransactionObject"),
          },
          fetchPolicy: "cache-first",
        })
        // A failed warm-up must not surface — the row's own query will retry.
        .catch(() => {});
    },
    [apolloClient],
  );
  // A row the pointer rests on, not every one it passes over.
  const restTimerRef = useRef<number | undefined>(undefined);
  const prefetchOnRest = useCallback(
    (identifier: string) => {
      window.clearTimeout(restTimerRef.current);
      restTimerRef.current = window.setTimeout(
        () => prefetchTriageDetail(identifier),
        TRIAGE_PREFETCH_REST_MS,
      );
    },
    [prefetchTriageDetail],
  );
  useEffect(() => () => window.clearTimeout(restTimerRef.current), []);

  // Keyboard triage walks the list with ↑ ↓, so keep the neighbours warm - the
  // payment open asks for its own.
  const selectedIndex = triageItems.findIndex(
    (item) => item.id === selectedTriageId,
  );
  const nextIdentifier =
    selectedIndex < 0
      ? null
      : (triageItems[selectedIndex + 1]?.identifier ?? null);
  const previousIdentifier =
    selectedIndex < 0
      ? null
      : (triageItems[selectedIndex - 1]?.identifier ?? null);
  useEffect(() => {
    for (const neighbor of [nextIdentifier, previousIdentifier]) {
      if (neighbor) {
        prefetchTriageDetail(neighbor);
      }
    }
  }, [nextIdentifier, prefetchTriageDetail, previousIdentifier]);

  const scopeRef = useRef<HTMLDivElement>(null);
  const triagePanelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // The search's own scope, keeping what is typed there: the queue's
  // single-key shortcuts ("r" to its suggestions, "c" its chat, "h" hold) are
  // the queue's, not its search's.
  const { ref: searchScopeRef } = useContextScope({ capturesInput: true });
  const suggestionsKeyboardEngagedRef = useRef(false);
  const pendingSuggestionsFocusRef = useRef(false);
  const pendingEnterSuggestionsAfterSelectRef = useRef(false);
  const [suggestionsEngaged, setSuggestionsEngaged] = useState(false);

  // The queue's list - or, Similar on, its payee's others in its place.
  const getTriageListScope = useCallback(() => {
    return scopeRef.current?.querySelector(
      isSimilarRef.current
        ? '[data-testid="triage-similar"] [data-testid="triage-book-like-list"]'
        : '[data-testid="triage-list"]',
    ) as HTMLElement | null;
  }, []);

  const handleNavigateTriage = useCallback((id: string) => {
    suggestionsKeyboardEngagedRef.current = false;
    pendingSuggestionsFocusRef.current = false;
    setSuggestionsEngaged(false);
    setSelectedTriageId(id);
    setReconcilingManually(null);
  }, []);

  const getSuggestionsListScope = useCallback(() => {
    // On the page, not under this view's element: the match panel is drawn
    // in the node panel.
    return document.querySelector(
      '[data-testid="triage-suggestions-options"]',
    ) as HTMLElement | null;
  }, []);

  const getSuggestionsPanelScope = useCallback(() => {
    return document.querySelector(
      '[data-testid="triage-suggestions-scope"]',
    ) as HTMLElement | null;
  }, []);

  const isSuggestionsKeyboardActive = useCallback(() => {
    if (suggestionsKeyboardEngagedRef.current) {
      return true;
    }
    const panel = getSuggestionsPanelScope();
    const active = document.activeElement;
    if (!panel || !active) return false;
    return panel === active || panel.contains(active);
  }, [getSuggestionsPanelScope]);

  const isTriageListKeyboardActive = useCallback(() => {
    if (isSuggestionsKeyboardActive()) return false;
    const active = document.activeElement;
    if (!active) return false;
    // Any nested focusable inside a triage row owns arrow / enter keys.
    const row = active.closest('[data-testid="triage-reconcile-row"]');
    if (row && row !== active) {
      return false;
    }
    if (triagePanelRef.current?.contains(active)) return true;
    if (scopeRef.current === active) return true;
    return false;
  }, [isSuggestionsKeyboardActive]);

  const focusSearch = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  const focusList = useCallback(() => {
    scopeRef.current?.focus();
  }, []);

  const focusTriageList = useCallback(() => {
    suggestionsKeyboardEngagedRef.current = false;
    pendingSuggestionsFocusRef.current = false;
    setSuggestionsEngaged(false);
    const listScope = getTriageListScope();
    if (listScope) {
      listScope.focus();
      return;
    }
    scopeRef.current?.focus();
  }, [getTriageListScope]);

  // Back to the queue from a payment's matches - ← or Escape from them - and
  // the keyboard with it. One at a time, the queue is drawn in view before it
  // is focused: nothing out of sight can be.
  const showQueue = useCallback(() => {
    flushSync(() => setIsMatchShown(false));
    focusTriageList();
  }, [focusTriageList]);

  const enterSuggestionsPanel = useCallback(() => {
    if (!selectedIdRef.current) {
      return;
    }
    // One at a time, the matches gone into are the ones in view.
    setIsMatchShown(true);
    // Leave nested row focus via the parent scope, then hand off to suggestions.
    scopeRef.current?.focus();
    enterSuggestionsRef.current?.();
  }, []);

  const enterSuggestionsForTriage = useCallback(
    (triageId: string) => {
      if (selectedIdRef.current !== triageId) {
        pendingEnterSuggestionsAfterSelectRef.current = true;
        setSelectedTriageId(triageId);
        return;
      }
      enterSuggestionsPanel();
    },
    [enterSuggestionsPanel],
  );

  const handleSelectTriage = useCallback(
    (id: string) => {
      setIsMatchShown(true);
      suggestionsKeyboardEngagedRef.current = false;
      pendingSuggestionsFocusRef.current = false;
      setSuggestionsEngaged(false);
      // One at a time on a computer, the queue goes out of sight behind the
      // matches picked: the keyboard goes into them, to come back by ← or
      // Escape. A phone has no keys to take there.
      const keyboardGoesIn = isOneAtATime && !isMobile;

      const reclaimListFocus = () => {
        const listScope = getTriageListScope();
        if (shouldDeferListFocusReclaim(listScope)) {
          return;
        }
        const panel = getSuggestionsPanelScope();
        const active = document.activeElement;
        if (panel && active instanceof HTMLElement && panel.contains(active)) {
          active.blur();
        }
        focusTriageList();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (shouldDeferListFocusReclaim(getTriageListScope())) {
              return;
            }
            focusTriageList();
          });
        });
      };

      // The payment picked already, clicked again, stays picked - the first,
      // picked as the queue opens, too: a click never lets it go, and the
      // keys come back to the queue on it. (Escape lets it go.)
      if (selectedIdRef.current !== id) {
        setSelectedTriageId(id);
        setReconcilingManually(null);
      }
      if (keyboardGoesIn) {
        enterSuggestionsForTriage(id);
      } else {
        reclaimListFocus();
      }
    },
    [
      enterSuggestionsForTriage,
      focusTriageList,
      getSuggestionsPanelScope,
      isMobile,
      isOneAtATime,
    ],
  );

  /** Accept & next: remove the current row (and peer JE legs) from the connection, then select the neighbor - the keyboard in its suggestions. */
  const acceptAndAdvance = useCallback(
    (options?: { alsoRemoveTransactionIdentifiers?: string[] }) => {
      const currentId = selectedIdRef.current;
      if (!currentId) {
        return;
      }

      const items = triageItemsRef.current;
      const peerIdentifiers = new Set(
        options?.alsoRemoveTransactionIdentifiers ?? [],
      );
      const removeIds = new Set<string>([currentId]);
      for (const item of items) {
        if (
          item.id !== currentId &&
          item.identifier &&
          peerIdentifiers.has(item.identifier)
        ) {
          removeIds.add(item.id);
        }
      }

      const idx = items.findIndex((item) => item.id === currentId);
      let nextId: string | null = null;
      let nextIdentifier: string | null = null;
      if (idx >= 0) {
        for (let i = idx + 1; i < items.length; i++) {
          if (!removeIds.has(items[i].id)) {
            nextId = items[i].id;
            nextIdentifier = items[i].identifier ?? null;
            break;
          }
        }
        if (nextId == null) {
          for (let i = idx - 1; i >= 0; i--) {
            if (!removeIds.has(items[i].id)) {
              nextId = items[i].id;
              nextIdentifier = items[i].identifier ?? null;
              break;
            }
          }
        }
      }

      removeNodesFromConnections(
        apolloClient.cache,
        "triageConnection",
        removeIds,
        "TransactionObject",
      );

      setJournalPreviewResolved(EMPTY_TRIAGE_JOURNAL_RESOLVED);
      setReconcilingManually(null);
      setSelectedTriageId(nextId);
      suggestionsKeyboardEngagedRef.current = false;
      pendingSuggestionsFocusRef.current = false;
      setSuggestionsEngaged(false);

      if (nextIdentifier) {
        prefetchTriageDetail(nextIdentifier);
      }
      // Booked, on to the next one's suggestions - the keyboard on them as they
      // are drawn - as Enter from the queue goes into them.
      if (nextId) {
        pendingEnterSuggestionsAfterSelectRef.current = true;
        return;
      }

      const reclaimListFocus = () => {
        if (shouldDeferListFocusReclaim(getTriageListScope())) {
          return;
        }
        focusTriageList();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (shouldDeferListFocusReclaim(getTriageListScope())) {
              return;
            }
            focusTriageList();
          });
        });
      };

      queueMicrotask(reclaimListFocus);
    },
    [apolloClient, focusTriageList, getTriageListScope, prefetchTriageDetail],
  );

  const removeFromQueue = useCallback(
    (identifiers: string[]) =>
      removeNodesFromConnections(
        apolloClient.cache,
        "triageConnection",
        identifiers.map((identifier) =>
          identifierAndTypenameToId(identifier, "TransactionObject"),
        ),
        "TransactionObject",
      ),
    [apolloClient],
  );

  const holdOffQueue = useCallback(
    (identifiers: string[]) => {
      setHeldOff((held) => new Set([...held, ...identifiers]));
      removeFromQueue(identifiers);
      return () =>
        setHeldOff((held) => {
          const left = new Set(held);
          for (const identifier of identifiers) left.delete(identifier);
          return left;
        });
    },
    [removeFromQueue],
  );

  const deselectTriage = useCallback(() => {
    suggestionsKeyboardEngagedRef.current = false;
    pendingSuggestionsFocusRef.current = false;
    setSuggestionsEngaged(false);
    setJournalPreviewResolved(EMPTY_TRIAGE_JOURNAL_RESOLVED);
    setSelectedTriageId(null);
    // Keep list keyboard-ready without leaving a selected/focused row.
    queueMicrotask(() => {
      getTriageListScope()?.focus();
    });
  }, [getTriageListScope]);

  const isFocusWithinSelectedTriageRow = useCallback(() => {
    const triageId = selectedIdRef.current;
    const active = document.activeElement;
    if (!triageId || !active) {
      return false;
    }
    const row = document.querySelector(
      `[data-testid="triage-reconcile-row"][data-triage-id="${triageId}"]`,
    );
    return Boolean(row?.contains(active));
  }, []);

  const focusSuggestionsList = useCallback(() => {
    const scope = getSuggestionsListScope();
    if (!scope) {
      return false;
    }
    scope.focus();
    suggestionsKeyboardEngagedRef.current = true;
    pendingSuggestionsFocusRef.current = false;
    return true;
  }, [getSuggestionsListScope]);

  const [suggestionsFocusEpoch, setSuggestionsFocusEpoch] = useState(0);

  const engageSuggestions = useCallback(() => {
    pendingSuggestionsFocusRef.current = true;
    setSuggestionsEngaged(true);
    setSuggestionsFocusEpoch((value) => value + 1);
  }, []);

  const openChatForTriage = useCallback(
    (triageId: string) => {
      const state = State.getState();
      if (state.isThreadPanelOpen && selectedIdRef.current === triageId) {
        state.setIsThreadPanelOpen(false);
        return;
      }

      if (selectedIdRef.current !== triageId) {
        setSelectedTriageId(triageId);
      }
      // Prefer the list scope so Escape returns keyboard nav (j/k / Enter),
      // not the Chat pill (which is ``tabIndex={-1}`` once focus leaves the row).
      const listScope = getTriageListScope();
      const row = document.querySelector(
        `[data-testid="triage-reconcile-row"][data-triage-id="${triageId}"]`,
      ) as HTMLElement | null;
      const restoreEl =
        listScope ?? row ?? (document.activeElement as HTMLElement | null);
      const contextNode = restoreEl ? findNearestContextNode(restoreEl) : null;
      state.pushFocusOrigin({ contextNode, elementRef: restoreEl });
      state.openThreadPanel({ autofocus: true });
    },
    [getTriageListScope],
  );

  useEffect(() => {
    if (!pendingEnterSuggestionsAfterSelectRef.current) {
      return;
    }
    if (!selectedTriageId) {
      return;
    }
    pendingEnterSuggestionsAfterSelectRef.current = false;
    enterSuggestionsPanel();
  }, [enterSuggestionsPanel, selectedTriageId]);

  useEffect(() => {
    if (!pendingSuggestionsFocusRef.current || !suggestionsEngaged) {
      return;
    }

    const attemptFocus = () => focusSuggestionsList();

    if (attemptFocus()) {
      return;
    }

    const root = scopeRef.current;
    if (!root) {
      return;
    }

    const observer = new MutationObserver(() => {
      attemptFocus();
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [
    selectedTriageId,
    focusSuggestionsList,
    suggestionsEngaged,
    suggestionsFocusEpoch,
  ]);

  const handleAcceptKey = useCallback(() => {
    acceptHandlerRef.current?.();
  }, []);

  // Put on hold or taken off it at once: the row the server will answer with
  // stands in for it until it does (``optimisticResponse``), and a refusal
  // takes it back. The payment stays selected either way.
  const setHeld = useCallback(
    (triageId: string, isHeld: boolean) => {
      const item = triageItemsRef.current.find((row) => row.id === triageId);
      if (!item) return;
      const field = isHeld
        ? "holdTriageTransaction"
        : "unholdTriageTransaction";
      (isHeld ? holdTriageTransaction : unholdTriageTransaction)({
        variables: { input: { transactionIdentifier: item.identifier } },
        optimisticResponse: {
          [field]: {
            __typename: isHeld
              ? "HoldTriageTransactionPayload"
              : "UnholdTriageTransactionPayload",
            errors: null,
            data: {
              __typename: "TransactionObject",
              id: item.id,
              triageHeldAt: isHeld ? new Date().toISOString() : null,
              triageHoldReason: isHeld ? item.triageHoldReason : null,
              triageHoldUntil: null,
              triageHeldForViewer: isHeld ? item.triageHeldForViewer : false,
            },
          },
        },
      });
    },
    [holdTriageTransaction, unholdTriageTransaction],
  );

  const holdTriage = useCallback(
    (triageId: string) => setHeld(triageId, true),
    [setHeld],
  );

  // The key puts the selected payment on hold, or takes it off: one waiting
  // on the viewer's answer about a receipt is answered in its notice instead.
  const handleHold = useCallback(() => {
    const item = triageItemsRef.current.find(
      (row) => row.id === selectedIdRef.current,
    );
    if (!item) return;
    if (!item.triageHeldAt) {
      holdTriage(item.id);
    } else if (!item.triageHeldForViewer) {
      setHeld(item.id, false);
    }
  }, [holdTriage, setHeld]);

  // Similar turned on - the keyboard on its payee's others as they are drawn
  // in the queue's place (``TriageDetail``) - or off, back on the queue.
  const setSimilar = useCallback(
    (on: boolean) => {
      if (on && !isSimilarRef.current) {
        const at = measureQueueRow();
        queueRowTopRef.current =
          at?.top != null ? at.top - at.scroller.scrollTop : null;
        // And the rows on screen, by their payments.
        const bounds = at?.scroller.getBoundingClientRect();
        queueSeenRef.current = [
          ...(at?.scroller.querySelectorAll<HTMLElement>(
            "[data-selectable-row]",
          ) ?? []),
        ]
          .filter((each) => {
            const box = each.getBoundingClientRect();
            return (
              bounds != null &&
              box.bottom > bounds.top &&
              box.top < bounds.bottom
            );
          })
          .map((each) =>
            each
              .querySelector("[data-triage-id]")
              ?.getAttribute("data-triage-id"),
          );
      }
      isSimilarRef.current = on;
      setSimilarOf(on ? selectedIdRef.current : null);
      if (!on) requestAnimationFrame(focusTriageList);
    },
    [focusTriageList, measureQueueRow],
  );

  const { Scope } = useContextScope({
    hotkeys: [
      {
        // The payee's others in the queue's place, to do to them all at once
        // what is done to it - and back.
        id: "triage-book-like",
        combos: TRIAGE_KEYS.BOOK_LIKE,
        callback: () => setSimilar(!isSimilarRef.current),
        isEnabled: () => !!selectedIdRef.current,
      },
      {
        id: "triage-accept-meta",
        combos: TRIAGE_KEYS.ACCEPT_META,
        callback: handleAcceptKey,
        // Do not gate on ``openResetHotkeyOverlays``: the suggestions panel
        // stays mounted with ``resetHotkeys`` whenever a triage is selected,
        // which would permanently disable ⌘↵ / Ctrl+Enter.
        isEnabled: () => !!selectedIdRef.current,
      },
      {
        id: "triage-hold",
        combos: TRIAGE_KEYS.HOLD,
        callback: handleHold,
        isEnabled: () => !!selectedIdRef.current,
      },
      {
        // Match's own actions, in the toolbar: its mailboxes and forwarding
        // address, and its suggestions worked out again.
        id: "triage-actions",
        combos: TRIAGE_KEYS.ACTIONS,
        callback: () => {
          const selector =
            '[data-testid="triage-group-actions"][data-group="match"]';
          const menu = document.querySelector<HTMLElement>(selector);
          if (menu) {
            menu.click();
            return;
          }
          // The bar short of room, the kinds behind its "⋯": opened there.
          document
            .querySelector<HTMLElement>(
              '[data-testid="triage-group-menus-more"]',
            )
            ?.click();
          requestAnimationFrame(() =>
            document.querySelector<HTMLElement>(selector)?.click(),
          );
        },
        isEnabled: () => !!selectedIdRef.current,
      },
      {
        // The payment's own Move, over its suggestions - as from them.
        id: "triage-move",
        combos: TRIAGE_KEYS.MOVE,
        callback: () =>
          document
            .querySelector<HTMLElement>(
              'button[data-testid="triage-payment-move"]',
            )
            ?.click(),
        isEnabled: () => !!selectedIdRef.current,
      },
      {
        // The open payment's tags, on its row.
        id: "triage-tags",
        combos: TRIAGE_KEYS.TAGS,
        callback: openTags,
        isEnabled: () => !!selectedIdRef.current,
      },
      {
        id: "triage-reconcile",
        combos: TRIAGE_KEYS.RECONCILE,
        callback: () => {
          const triageId = selectedIdRef.current;
          if (triageId) {
            enterSuggestionsForTriage(triageId);
          }
        },
        isEnabled: () =>
          !!selectedIdRef.current && isTriageListKeyboardActive(),
      },
      {
        // The selected payment's entry, opened and shut from the queue.
        id: "triage-preview",
        combos: TRIAGE_KEYS.PREVIEW,
        callback: () =>
          document
            .querySelector<HTMLElement>('[data-testid="triage-preview"]')
            ?.click(),
        isEnabled: () =>
          !!selectedIdRef.current && isTriageListKeyboardActive(),
      },
      {
        id: "triage-add-journal-line",
        combos: TRIAGE_KEYS.ADD_LINE,
        callback: () => {
          const triageId = selectedIdRef.current;
          if (!triageId) {
            return;
          }
          const addRowAffordance = document.querySelector(
            `[data-testid="triage-reconcile-row"][data-triage-id="${triageId}"] [data-testid="field-table-add-row"]`,
          ) as HTMLElement | null;
          addRowAffordance?.click();
        },
        isEnabled: () => {
          const triageId = selectedIdRef.current;
          if (!triageId || !isTriageListKeyboardActive()) {
            return false;
          }
          return Boolean(
            document.querySelector(
              `[data-testid="triage-reconcile-row"][data-triage-id="${triageId}"] [data-testid="field-table-add-row"]`,
            ),
          );
        },
      },
      {
        id: "triage-tab-to-search",
        combos: "Tab",
        callback: focusSearch,
        isEnabled: () => {
          const active = document.activeElement;
          if (active === searchInputRef.current) {
            return false;
          }
          // Row handles Tab among its own focusables until the last one.
          if (isFocusWithinSelectedTriageRow()) {
            const row = document.querySelector(
              `[data-testid="triage-reconcile-row"][data-triage-id="${selectedIdRef.current}"]`,
            );
            if (row) {
              const focusables = Array.from(
                row.querySelectorAll<HTMLElement>(
                  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                ),
              ).filter((element) => element.getClientRects().length > 0);
              const index = active
                ? focusables.indexOf(active as HTMLElement)
                : -1;
              if (index >= 0 && index < focusables.length - 1) {
                return false;
              }
            }
          }
          return true;
        },
      },
      {
        id: "triage-enter-detail",
        combos: "Enter",
        // Into what is suggested for the payment: the row has no controls of
        // its own to go into.
        callback: enterSuggestionsPanel,
        isEnabled: () =>
          !!selectedIdRef.current && isTriageListKeyboardActive(),
      },
      {
        id: "triage-arrow-into-suggestions",
        combos: "ArrowRight",
        callback: enterSuggestionsPanel,
        isEnabled: () =>
          !!selectedIdRef.current && isTriageListKeyboardActive(),
      },
      {
        id: "triage-escape-deselect",
        combos: "Escape",
        callback: deselectTriage,
        // Only when the list owns keyboard (selected row focused). Disabled
        // while focus is inside the row — ``isTriageListKeyboardActive`` is
        // already false there. Do not gate on ``openResetHotkeyOverlays``:
        // the suggestions panel stays mounted with ``resetHotkeys`` whenever
        // a triage is selected, which would permanently disable this.
        isEnabled: () =>
          !!selectedIdRef.current && isTriageListKeyboardActive(),
      },
    ],
  });

  // The payment picked, and those either side of it in the queue - which a
  // phone's pinned payment slides in from.
  const { selectedTriage, paymentBefore, paymentAfter } = useMemo(() => {
    const index = triageItems.findIndex((item) => item.id === selectedTriageId);
    return index < 0
      ? { selectedTriage: null, paymentBefore: null, paymentAfter: null }
      : {
          selectedTriage: triageItems[index],
          paymentBefore: triageItems[index - 1] ?? null,
          paymentAfter: triageItems[index + 1] ?? null,
        };
  }, [triageItems, selectedTriageId]);

  const triageWorkbench = useMemo(() => {
    return (
      <TriageWorkbench
        panelRef={triagePanelRef}
        items={queueItems}
        listKey={triageListKey}
        loading={loading && triageItems.length === 0}
        selectedId={selectedTriageId}
        onSelect={handleSelectTriage}
        onNavigate={handleNavigateTriage}
        onPrefetch={prefetchOnRest}
        isTriageListKeyboardActive={isTriageListKeyboardActive}
        // Narrowed to a payee's others, or their rows coming back, the queue
        // is short for a while, not at its end: no more read, and no
        // placeholders drawn for them, in the frames it moves.
        loadMore={queueItems === triageItems ? triageLoadMore : undefined}
        activeRowStaging={activeRowStaging}
        // Narrowed to a payee's others, as Similar opens or closes: their
        // count goes as the rest do, and comes back with them.
        isFillingIn={isQueueFillingIn}
      />
    );
  }, [
    isQueueFillingIn,
    similarRows,
    queueItems,
    triageItems,
    triageListKey,
    handleNavigateTriage,
    handleSelectTriage,
    isTriageListKeyboardActive,
    loading,
    prefetchOnRest,
    selectedTriageId,
    triageLoadMore,
    activeRowStaging,
  ]);
  // The queue - or, Similar on, the payment's payee's others in its place:
  // the queue kept as it was under them, to come back to. Over either, the
  // one bar (``triageQueueToolbar``) - its filters narrowing whichever is
  // shown - so nothing in it moves as Similar is turned on and off.
  const triageLayoutContent = (
    similarList: ReactNode | null,
    isSimilarShown: boolean,
  ) => (
    <>
      {similarList}
      <div
        className={cn(
          "flex-1 min-h-0 min-w-0",
          isSimilarShown ? "hidden" : "flex",
          "flex-col",
        )}
      >
        {triageWorkbench}
      </div>
    </>
  );
  // How many of its payee's others the payment picked has: Similar's count.
  const similarCount = selectedTriage?.payeePaymentCount ?? 0;

  // The queue's search, in the queue's toolbar - the layout's, so it is
  // there while the queue loads as much as once it has.
  // The queue's one row: its search, a field taking what the rest leaves it,
  // then its Filter, then how it is shown.
  const triageQueueToolbar = (
    like: Parameters<typeof TriageFilterGroup>[0]["like"],
  ) => (
    <div
      data-testid="triage-queue-toolbar"
      className="flex-1 min-w-0 flex items-center pl-2 pr-2 gap-2"
    >
      {/* No box of its own: the bar is its box, and Similar's chip in it
          the one thing drawn as one. */}
      <InputGroup
        ref={searchScopeRef}
        // Its words a button's words' inset (``sm``'s 12px) from what is
        // before them - the bar's edge, or Similar's chip.
        className="border-0 bg-transparent shadow-none dark:bg-transparent has-[[data-slot=input-group-control]:focus-visible]:ring-0 h-8 flex-1 min-w-30 has-[>[data-align=inline-start]]:[&>input]:pl-3"
      >
        {isSimilar && selectedTriage ? (
          <InputGroupAddon className="pl-0">
            {/* Similar's mark and its x - L, or Backspace at the
                search's start, as well. */}
            <TriageSearchChip
              data-testid="triage-similar-chip"
              startDecorator={<Icon ReactIcon={LayersIcon} size="md" />}
              removeLabel="Close Similar"
              removeTestId="triage-similar-close"
              onRemove={() => setSimilar(false)}
              className="shrink-0"
            >
              {selectedTriage.description ?? "This payment"}
            </TriageSearchChip>
          </InputGroupAddon>
        ) : null}
        <InputGroupInput
          ref={searchInputRef}
          data-testid="triage-queue-search"
          placeholder="Search for payments…"
          className="pl-3 pr-0"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setSearchFocus("queue")}
          onBlur={() => setSearchFocus((was) => (was === "queue" ? null : was))}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              focusList();
            }
            // To the panel's search.
            if (e.key === "Tab" && !e.shiftKey) {
              const other = document.querySelector<HTMLInputElement>(
                '[data-testid="triage-suggestions-search"]',
              );
              if (other && !other.disabled) {
                e.preventDefault();
                other.focus();
              }
            }
            // At its start, Similar's chip let go - as the panel's search lets
            // go of its own.
            const input = e.target as HTMLInputElement;
            if (
              e.key === "Backspace" &&
              isSimilarRef.current &&
              input.selectionStart === 0 &&
              input.selectionEnd === 0
            ) {
              e.preventDefault();
              setSimilar(false);
            }
          }}
        />
        <InputGroupAddon align="inline-end">
          {
            <>
              {/* The keyboard in the panel's search: Tab comes here. */}
              {searchFocus === "suggestions" ? (
                <div
                  data-testid="triage-queue-search-tab-hint"
                  className="flex opacity-[0.64] mr-1.5"
                >
                  <HotkeyDecorator hotkey="tab" color="inherit" />
                </div>
              ) : null}
            </>
          }
        </InputGroupAddon>
      </InputGroup>
      <TriageFilterGroup
        value={queueFilters}
        onChange={setQueueFilters}
        entityIdentifiers={activeEntityIdentifiers}
        like={like}
      />
      <TriageQueueView />
    </div>
  );

  // Every row's chrome consumes this context, so a value that changes
  // re-renders every row regardless of ``TriageReconcileRow``'s memo - and
  // its callbacks change with nearly everything the view does (Similar on or
  // off redrew the whole queue in the frame it began to move). So the value
  // is made once, each action calling the one this render has.
  const latestTriageViewActions = {
    onResolved: acceptAndAdvance,
    holdTriage,
    takeOffHold: (triageId: string) => setHeld(triageId, false),
    registerActiveRowStaging,
    registerAccept,
    triggerAccept: handleAcceptKey,
    registerEnterSuggestions,
    enterSuggestions: enterSuggestionsForTriage,
    openChat: openChatForTriage,
    registerOpenTags,
    openTags,
    deselectTriage,
    focusTriageList,
    removeFromQueue,
    holdOffQueue,
    findDocumentFor: (triageId: string) => {
      handleSelectTriage(triageId);
      setReconcilingManually("document");
    },
    openSimilar: () => setSimilar(true),
  };
  const triageViewActionsRef = useRef(latestTriageViewActions);
  // Before any effect of the rows' runs, so none calls one a render behind.
  useInsertionEffect(() => {
    triageViewActionsRef.current = latestTriageViewActions;
  });
  const triageViewActions = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(triageViewActionsRef.current).map((name) => [
          name,
          (...args: never[]) =>
            (
              triageViewActionsRef.current[
                name as keyof typeof latestTriageViewActions
              ] as (...args: never[]) => unknown
            )(...args),
        ]),
      ) as typeof latestTriageViewActions,
    [],
  );

  const triageLayout = (
    <TriageDetail
      listItem={selectedTriage}
      entityIdentifiers={activeEntityIdentifiers}
      onEscape={showQueue}
      onEngageSuggestions={engageSuggestions}
      isSuggestionsKeyboardActive={isSuggestionsKeyboardActive}
      suggestionsEngaged={suggestionsEngaged}
      reconcilingManually={reconcilingManually}
      onReconcilingManuallyChange={setReconcilingManually}
      onJournalResolvedChange={handleJournalResolvedChange}
      layoutContent={triageLayoutContent}
      queueFilter={triageFilter ?? null}
      queueToolbar={triageQueueToolbar}
      queueLoading={loading && triageItems.length === 0}
      isOneAtATime={isOneAtATime}
      isMatchShown={isMatchShown && selectedTriage != null}
      // On a computer the keyboard comes back to the queue with it; a phone
      // has none to bring.
      onShowQueue={isMobile ? () => setIsMatchShown(false) : showQueue}
      paymentBefore={paymentBefore}
      paymentAfter={paymentAfter}
      onSwipeToPayment={(direction) => {
        const next = direction === 1 ? paymentAfter : paymentBefore;
        if (next) handleNavigateTriage(next.id);
      }}
      isSimilar={isSimilar}
      onSimilarChange={setSimilar}
      onSimilarRows={setSimilarRows}
      searchFocus={searchFocus}
      onSuggestionsSearchFocus={(isFocused) =>
        setSearchFocus((was) =>
          isFocused ? "suggestions" : was === "suggestions" ? null : was,
        )
      }
    />
  );

  return (
    <TriageViewActionsContext.Provider value={triageViewActions}>
      <Scope
        ref={scopeRef}
        tabIndex={-1}
        data-testid="triage-scope"
        style={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          outline: "none",
        }}
      >
        <div
          ref={measuredRef}
          className="flex-1 min-w-0 min-h-0 flex flex-row overflow-hidden"
        >
          {triageLayout}
        </div>
      </Scope>
    </TriageViewActionsContext.Provider>
  );
}
